import ExtractedArtifactsSection from './Sections/ExtractedArtifactsSection';
import EventSection from './Sections/EventSection';
import { recordMessageView } from '../../util/recordMessageView';
import { hasMarkdownFormatting, linkifyMarkdownTitle, getValidMarkdownAnchorProps } from '../../util/titleText';
import MessageSharingIndicator from '../Sharing/MessageSharingIndicator';
import { decodeDisplayText } from '../../util/displayText';
import {useTranslation} from 'react-i18next';
import { getFolderAccess } from '../../util/folderAccess';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { format, compareAsc, isToday, formatDistanceToNowStrict } from 'date-fns';
import Markdown, { compiler } from 'markdown-to-jsx';

import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled, { css } from 'styled-components';
import ItemTypes from '../ItemTypes';

import { FaRegStickyNote } from 'react-icons/fa';
import { MdLock, MdFolderOpen, MdNotificationsActive } from 'react-icons/md';
import { FiBell, FiEdit2, FiX } from 'react-icons/fi';
import { IoMdMenu, IoMdHeart, IoMdRepeat, IoMdChatbubbles, IoMdPricetags, IoMdTime } from 'react-icons/io';

import URLPreviewSection from './Sections/URLPreviewSection';
import EmailPreviewPlugin from '../MessagePreview/Plugins/EmailPreviewPlugin';
import CarouselModal from '../Modals/CarouselModal';
import ImageSection from './Sections/ImageSection';
import FileSection from './Sections/FileSection';
// import ImageFileSection from './Sections/ImageFileSection';
import VideoSection from './Sections/VideoSection';
import AudioSection from './Sections/AudioSection/AudioSection';
import CheckboxSection from './Sections/CheckboxSection';
import { AuthContext } from '../../util/context';

import { ENTITY_COLORS, NOTE_BACKGROUND, PRIMARY_COLOR_6, PRIMARY_COLOR_8, PALE_WHITE_1, DETAIL_TEXT, PRIMARY_COLOR_4, STARRED, GREEN, GREEN_OP, GREEN_OP_2, FILE_BLUE, WHITE, SECONDARY_TEXT } from '../../appearance/Colors';

import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { MUTATION, QUERY } from '../../gql-operations';
import MessageReferencePreview from '../MessageReferencePreview';

import ReactTooltip from 'react-tooltip';
import MessageDropdown from './MessageDropdown';
import ConfirmModal from '../Modals/ConfirmModal';
import ReminderPickerModal from '../Modals/ReminderPickerModal';
import customAnalytics from '../../util/custom-analytics';
import { isFileTypeImage } from '../../constants/fileUtils';
import { tokenizeInteractiveText } from '../../util/textPatterns';
import EmbedSection from './Sections/EmbedSection';
import SocialNetworkModal from './SocialNetworkModal';
import StockTickerModal from './StockTickerModal';
import { LAYOUT, calculateVideoFrameBounds, calculateImageFrameBounds } from './layoutBreakpoints';
import {
    createMarkdownToJsxOptions,
    normalizeMarkdownForStorage,
    shouldRenderMarkdownAsPlainText
} from '../../util/markdownTheme';
import { getUrlMetaVideoSource } from '../../util/url-meta-media';

import useRedirectErrors from '../../errors/useRedirectErrors';
import { CacheManager } from '../../util/cacheManager';
import { getReminderDueTimezone, REMINDER_WEB_INSTALLATION_ID } from '../../util/reminderClientContext';
import useNotificationSystem from '../Notifications/useNotificationSystem';
import EntityBadges, { ENTITY_LABELS, ENTITY_PLURAL_LABELS, isMentionEntity } from './EntityBadges';
import EntityMentionsSection from '../MessagePreview/EntityMentionsSection';
import Modal from '../Modals/Modal';
import { getSearchUrl, getStoredSearchEngine } from '../../constants/searchEngines';
import { openExternalUrl } from '../../utils/tauri';
import {
    buildSearchMatchPreviewExcerpt,
    countSearchMatches,
    highlightReactChildren,
    renderHighlightedSearchText,
    stripMarkdownForPlainText
} from '../../util/searchHighlight';
import {
    selectHiddenFieldSearchMatch
} from '../../util/searchMatchSnippets';

// Rough character-offset heuristic: the CSS-truncated card preview shows
// roughly this many characters before it clips. If the first search match
// falls beyond it, the from-the-top preview would hide the match entirely,
// so we add a separate match-centered excerpt row below the normal preview.
const SEARCH_MATCH_VISIBLE_WINDOW = 300;
const SEARCH_MATCH_EXCERPT_LENGTH = 220;
const hasArtifactContent = message => {
    const attachedArtifacts = Array.isArray(message?.artifacts) ? message.artifacts : [];
    if (attachedArtifacts.some(artifact => String(artifact?.title || artifact?.summary || artifact?.type || '').trim())) {
        return true;
    }

    if (Array.isArray(message?.artifactIds) && message.artifactIds.length > 0) {
        return true;
    }

    const legacyContent = message?.structuredContent;
    return Boolean(
        String(legacyContent?.title || legacyContent?.summary || '').trim() ||
        (Array.isArray(legacyContent?.sections) && legacyContent.sections.some(section =>
            Array.isArray(section?.entries) && section.entries.length > 0
        ))
    );
};

const getArtifactTypes = message => {
    const artifactTypes = Array.isArray(message?.artifacts)
        ? message.artifacts
              .map(artifact => String(artifact?.type || '').trim().toLowerCase() === 'repair' ? 'tutorial' : String(artifact?.type || '').trim())
              .filter(Boolean)
        : [];

    if (artifactTypes.length === 0 && message?.structuredContent?.type) {
        artifactTypes.push(String(message.structuredContent.type));
    }

    return Array.from(new Set(artifactTypes));
};

const SearchMatchSnippet = ({ message, searchTerm, hiddenFields }) => {
    const term = String(searchTerm || '').trim();
    const match = selectHiddenFieldSearchMatch(message, searchTerm, hiddenFields);
    if (!match) {
        return null;
    }

    return (
        <SearchSnippetRow>
            <SearchSnippetLabel>{match.label}</SearchSnippetLabel>
            <SearchSnippetText>{renderHighlightedSearchText(match.snippet, term)}</SearchSnippetText>
        </SearchSnippetRow>
    );
};

const SearchBodyMatchSnippet = ({ excerpt, searchTerm }) => {
    const {t: lnT} = useTranslation();

    const term = String(searchTerm || '').trim();
    if (!excerpt || term.length < 2) {
        return null;
    }

    return (
        <SearchSnippetRow>
            <SearchSnippetLabel>{lnT('messages:note')}</SearchSnippetLabel>
            <SearchSnippetText>
                {excerpt.hasPrefix ? <SearchMatchExcerptPrefix>&hellip;</SearchMatchExcerptPrefix> : null}
                {renderHighlightedSearchText(excerpt.text, term)}
                {excerpt.hasSuffix ? <SearchMatchExcerptPrefix>&hellip;</SearchMatchExcerptPrefix> : null}
            </SearchSnippetText>
        </SearchSnippetRow>
    );
};

const entityMentionsModalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: 'min(92vw, 520px)',
        maxHeight: '80vh',
        padding: 0,
        overflow: 'auto',
        borderRadius: 16,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        background: '#222729',
        boxShadow: '0 24px 80px rgba(0, 0, 0, 0.48)'
    }
};

const ExpandableLinkText = ({
    href,
    text,
    title,
    onClick,
    maxLines = 2,
    isCollapsible = false,
    isExpanded = false,
    onToggle
}) => {
    const {t: lnT} = useTranslation();

    const textRef = useRef(null);
    const [hasOverflow, setHasOverflow] = useState(false);

    useEffect(() => {
        if (!isCollapsible) {
            setHasOverflow(false);
            return undefined;
        }

        // Keep the toggle mounted while expanded. Measuring an expanded node
        // would always report no overflow and immediately hide the control.
        if (isExpanded) {
            return undefined;
        }

        const measureOverflow = () => {
            const textNode = textRef.current;
            if (!textNode || typeof window === 'undefined') {
                setHasOverflow(false);
                return;
            }

            const nextHasOverflow = textNode.scrollHeight > textNode.clientHeight + 1;
            setHasOverflow(previousValue =>
                previousValue === nextHasOverflow ? previousValue : nextHasOverflow
            );
        };

        const animationFrameId = window.requestAnimationFrame(measureOverflow);
        const resizeObserver =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => {
                    measureOverflow();
                })
                : null;

        if (resizeObserver && textRef.current) {
            if (textRef.current.parentElement) {
                resizeObserver.observe(textRef.current.parentElement);
            }
        }

        window.addEventListener('resize', measureOverflow);

        return () => {
            window.cancelAnimationFrame(animationFrameId);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            window.removeEventListener('resize', measureOverflow);
        };
    }, [isCollapsible, isExpanded, maxLines, text]);

    return (
        <ExpandableLinkContainer data-message-interactive="true">
            <LinkButton
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                title={title}
            >
                <ClampedLinkText
                    ref={textRef}
                    $isExpanded={!isCollapsible || isExpanded}
                    $maxLines={maxLines}
                >
                    {text}
                </ClampedLinkText>
            </LinkButton>
            {isCollapsible && hasOverflow ? (
                <LinkExpandToggle
                    type="button"
                    data-message-interactive="true"
                    onMouseDown={event => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (typeof onToggle === 'function') {
                            onToggle();
                        }
                    }}
                >
                    {isExpanded ? lnT('messages:showLess') : lnT('messages:showMore')}
                </LinkExpandToggle>
            ) : null}
        </ExpandableLinkContainer>
    );
};

export const SHOW_OPTIONS = {
    SHOW_FOLDER: 'SHOW_FOLDER'
};

let defaultMessageInstanceCounter = 0;

// Global state to track which message instance is currently being hovered
let hoveredMessageId = null;
const hoverListeners = new Set();

const setHoveredMessage = (messageId) => {
    const previousId = hoveredMessageId;
    hoveredMessageId = messageId;
    // Notify all listeners that the hover state changed
    hoverListeners.forEach(listener => listener(previousId, messageId));
};

const getHoveredMessage = () => hoveredMessageId;

const addHoverListener = (listener) => {
    hoverListeners.add(listener);
    return () => hoverListeners.delete(listener);
};

// Global state to track which message has its dropdown menu open
let openMenuMessageId = null;
const menuListeners = new Set();

const setOpenMenuMessage = (messageId) => {
    const previousId = openMenuMessageId;
    openMenuMessageId = messageId;
    // Notify all listeners that the menu state changed
    menuListeners.forEach(listener => listener(previousId, messageId));
};

const getOpenMenuMessage = () => openMenuMessageId;

const addMenuListener = (listener) => {
    menuListeners.add(listener);
    return () => menuListeners.delete(listener);
};

const isGridCardLayout = layoutMode => layoutMode === 'masonry' || layoutMode === 'board';
const getUrlPreviewStackLayoutMaxWidth = (layoutMode, metaData) => {
    if (layoutMode === 'masonry') {
        const hasLargePreviewMedia = Boolean(
            getUrlMetaVideoSource(metaData) ||
            metaData?.image ||
            metaData?.gallery_images?.length
        );
        const hasAuthorProfilePreview = Boolean(
            metaData?.author_image || metaData?.author_image_original
        );

        if (hasLargePreviewMedia) {
            return 300;
        }

        if (hasAuthorProfilePreview) {
            return 220;
        }

        return 300;
    }

    if (isGridCardLayout(layoutMode)) {
        return 320;
    }

    return LAYOUT.PREVIEW_STACK_MAX;
};

const isServerBackedMessageId = id => /^[a-fA-F0-9]{24}$/.test(String(id || ''));
const toSafeTime = value => {
    if (!value) return 0;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
};

// Export for use in MessageDropdown
export { setOpenMenuMessage, getOpenMenuMessage, addMenuListener };

function EnrichmentCard({ entity, margin, layoutMode, hasContentAbove }) {
    const {t: lnT} = useTranslation();

    const [lightboxOpen, setLightboxOpen] = React.useState(false);
    const e = entity.enrichment;
    const isGrid = layoutMode === 'masonry' || layoutMode === 'board';
    const gridBleed = isGrid && !hasContentAbove;
    const aspectRatio = e.aspect_ratio || (e.height && e.width ? e.height / e.width : null);

    const posterEl = (
        <EntityEnrichmentPoster
            src={e.image}
            alt={entity.name}
            $isGrid={isGrid}
            $aspectRatio={aspectRatio}
            loading="lazy"
            onClick={ev => { ev.stopPropagation(); setLightboxOpen(true); }}
            data-message-interactive="true"
        />
    );

    const sourceEl = e.url ? (
        <EntityEnrichmentLink
            href={e.url}
            target="_blank"
            rel="noopener noreferrer"
            data-message-interactive="true"
            onClick={ev => ev.stopPropagation()}
        >
            {e.source === 'omdb' ? lnT('messages:imdb') : e.source}
        </EntityEnrichmentLink>
    ) : e.source ? (
        <EntityEnrichmentSource>{e.source === 'omdb' ? lnT('messages:imdb') : e.source}</EntityEnrichmentSource>
    ) : null;

    const cardStyle = { marginBottom: margin, marginTop: hasContentAbove ? MARGIN : 0 };

    return (
        <EntityEnrichmentCard $isGrid={isGrid} $gridBleed={gridBleed} style={cardStyle}>
            {posterEl}
            <EntityEnrichmentBody $isGrid={isGrid}>
                <EntityEnrichmentTitle>{entity.name}{e.year ? lnT('messages:text_ec422465', { value1: String(e.year) }) : ''}</EntityEnrichmentTitle>
                {e.description ? <EntityEnrichmentDesc $isGrid={isGrid}>{e.description}</EntityEnrichmentDesc> : null}
                {sourceEl}
            </EntityEnrichmentBody>
            {lightboxOpen && (
                <CarouselModal
                    isOpen={lightboxOpen}
                    closeModal={ev => { ev?.stopPropagation(); setLightboxOpen(false); }}
                    items={[{ src: e.image }]}
                    fileType="Link"
                    currentMessage={null}
                    messageId={null}
                />
            )}
        </EntityEnrichmentCard>
    );
}

const DefaultMessageContent = ({
    index,
    screenName,
    name,
    isDropped,
    message,
    carouselMessages,
    multipleSelect,
    imagePreview,
    showFolder,
    smallImage,
    messageFeatures,
    showDeleteModal,
    showMoveMessageModal,
    openMenu,
    showDateAtBottom,
    dateField = 'createdAt',
    showThreadHint,
    threadHintCounts = null,
    onThreadHintClick = null,
    threadHintActionLabel = 'Open thread',
    compactThreadHintSpacing = false,
    showThreadAction = true,
    disableThreadAttachTarget = false,
    hideCheckboxSection = false,
    hideEventArtifacts = false,
    onEditMessage,
    onReplyMessage,
    onShowInFolder,
    onMessageClick,
    selectionMapOverride = null,
    showMultiSelectOverride = null,
    layoutMode = 'list',
    hideQuickActions = false,
    hideMessageReference = false,
    suppressHover = false,
    showHoverPreviewAction = false,
    suppressSelectionHoverOutline = false,
    suppressBoardHoverHighlight = false,
    matchedEntityTypes = null,
    showEntityStackIcon = false,
    masonryCardFields = null,
    onOpenVideoWorkspace,
    openVideoInWorkspaceOnly = false,
    onCreateArticleAudio,
    isCreatingArticleAudio = false,
    activeSearchTerm = '',
    isSharedFolderView = false,
    sharedArtifactBadges = null,
    readOnly: forceReadOnly = false
}) => {
    const {t: lnT} = useTranslation();

    const messageRef = useRef();
    const {
        state: { holdingKeys, keys, selectedMessagesMap, showMultiSelect, messagesToSend, selectedFolder },
        context: {
            setSelectedMessage,
            setSelectedFolder,
            setSearchActive,
            setSearchTerm,
            setMessagesToSend,
            setSelectedMessagesMap,
            setShowMultiSelect,
            cancelUploadingMessage
        }
    } = React.useContext(AuthContext);
    const access = forceReadOnly
        ? { canEdit: false, canManage: false, canGenerate: false, canAdd: false, canReadThreads: false }
        : getFolderAccess(selectedFolder);
    const readOnly = !access.canEdit;
    // All layouts share these permissions, including masonry and board rows.
    hideQuickActions = hideQuickActions || readOnly;
    onReplyMessage = readOnly ? undefined : onReplyMessage;


    // Check if this is an uploading message
    const isUploading = message._isUploading || message._uploadProgress !== undefined;
    const uploadProgress = typeof message._uploadProgress === 'number' ? message._uploadProgress : 0;
    const uploadStatus = message._uploadStatus || 'uploading';
    const handleCancelUpload = React.useCallback(() => {
        if (typeof cancelUploadingMessage === 'function') {
            cancelUploadingMessage(message.id);
        }
    }, [lnT, cancelUploadingMessage, message.id]);
    const apolloClient = useApolloClient();
    const { passingErrors } = useRedirectErrors();
    // console.log('da fffff', holdingKeys, keys.Meta);

    let date = format(new Date(message[dateField] || message.createdAt), 'HH:mm');
    const effectiveReminderTimestamp = useMemo(() => {
        return message?.reminderAt || message?.reminderFiredAt || null;
    }, [lnT, message?.reminderAt, message?.reminderFiredAt]);
    const activeReminderLabel = useMemo(() => {
        if (!effectiveReminderTimestamp) {
            return null;
        }

        const reminderDate = new Date(effectiveReminderTimestamp);
        if (Number.isNaN(reminderDate.getTime())) {
            return null;
        }

        return isToday(reminderDate)
            ? format(reminderDate, 'HH:mm')
            : format(reminderDate, 'dd MMM, HH:mm');
    }, [lnT, effectiveReminderTimestamp]);
    const isFiredReminder = Boolean(!message?.reminderAt && message?.reminderFiredAt);
    const reminderNeedsReview = Boolean(message?.automaticReminderNeedsReview && !effectiveReminderTimestamp);

    const [getMessage] = useLazyQuery(QUERY.GET_MESSAGE);
    const [attachMessagesToThreadMutation] = useMutation(MUTATION.ATTACH_MESSAGES_TO_THREAD, {
        onError: error => passingErrors(error)
    });
    const [setMessageDueMutation, { loading: setMessageReminderLoading }] = useMutation(MUTATION.SET_MESSAGE_DUE);
    const [rawHoveringMessage, setHoveringMessage] = useState(false);
    const [rawHoveringContainer, setHoveringContainer] = useState(false);
    const [rawHoveringSelectTarget, setHoveringSelectTarget] = useState(false);
    const isHoverSuppressed = suppressHover;
    const hoveringMessage = rawHoveringMessage && !isHoverSuppressed;
    const hoveringContainer = rawHoveringContainer && !isHoverSuppressed;
    const hoveringSelectTarget = rawHoveringSelectTarget && !isHoverSuppressed;
    const [isThreadHintPinnedOpen, setIsThreadHintPinnedOpen] = useState(false);
    const [showSocialModal, setShowSocialModal] = useState(false);
    const [selectedUsername, setSelectedUsername] = useState('');
    const [showStockTickerModal, setShowStockTickerModal] = useState(false);
    const [selectedStockTicker, setSelectedStockTicker] = useState('');
    const [selectedEntityMentionGroup, setSelectedEntityMentionGroup] = useState(null);
    const [pendingThreadAttachMessages, setPendingThreadAttachMessages] = useState([]);
    const [showReminderPicker, setShowReminderPicker] = useState(false);
    const [sharingDialogOpen, setSharingDialogOpen] = useState(false);
    const [showEntityMentionsModal, setShowEntityMentionsModal] = useState(false);
    const searchHighlightTerm = String(activeSearchTerm || '').trim();
    // Folder/standalone AI chat session root — rendered as a distinct "conversation" card.
    // See docs/mastermind/folder-chat-visibility-design-2026-07-19.md (item c).
    const isConversationRoot = Boolean(message?.inChatSession && !message?.parentMessageId);
    const hasVideoAttachment = String(message.file?.type || '').includes('video');
    const isImageFile = isFileTypeImage(message.file?.type || '');
    const hasImageAttachment = !hasVideoAttachment && Boolean(message.image || isImageFile);
    const videoBubbleWidth = useMemo(() => {
        if (!hasVideoAttachment) return null;
        const bounds = calculateVideoFrameBounds(message.file);
        return bounds ? bounds.width + 18 : null;
    }, [hasVideoAttachment, message.file]);
    const imageBubbleWidth = useMemo(() => {
        if (!hasImageAttachment) return null;
        const targetMedia = message.image || message.file;
        const bounds = calculateImageFrameBounds(targetMedia);
        return bounds ? bounds.width + 18 : null;
    }, [hasImageAttachment, message.image, message.file]);
    const isMasonryLayout = layoutMode === 'masonry';
    const isBoardLayout = layoutMode === 'board';
    const isMasonryFieldVisible = fieldKey =>
        !isMasonryLayout || !masonryCardFields || masonryCardFields[fieldKey] !== false;
    const isUrlSectionVisibleForSearch = isMasonryFieldVisible('url');
    const isUrlMetaTitleVisibleForSearch =
        isUrlSectionVisibleForSearch && (!isMasonryLayout || !masonryCardFields || masonryCardFields.urlMetaTitle !== false);
    const isUrlMetaDescriptionVisibleForSearch =
        isUrlSectionVisibleForSearch && (!isMasonryLayout || !masonryCardFields || masonryCardFields.urlMetaDescription !== false);
    const searchSnippetHiddenFields = {
        caption: true,
        keywords: true,
        ocrText: true,
        documentText: true,
        urlMetaTitle: !isUrlMetaTitleVisibleForSearch,
        urlMetaDescription: !isUrlMetaDescriptionVisibleForSearch,
        fileDisplayName: !isMasonryFieldVisible('file'),
        imageDisplayName: true,
        urlMetaUrl: true
    };
    const [expandedTitleLinks, setExpandedTitleLinks] = useState({});
    const hasExpandedTitleLink = useMemo(
        () => Object.values(expandedTitleLinks).some(Boolean),
        [lnT, expandedTitleLinks]
    );
    const hoverInstanceIdRef = useRef(null);
    const { showDefaultNotification } = useNotificationSystem();

    if (!hoverInstanceIdRef.current) {
        defaultMessageInstanceCounter += 1;
        hoverInstanceIdRef.current = `${message?.id || 'message'}:${defaultMessageInstanceCounter}`;
    }

    const hoverInstanceId = hoverInstanceIdRef.current;

    const clearHoverState = React.useCallback(() => {
        setHoveringMessage(false);
        setHoveringContainer(false);
        setHoveringSelectTarget(false);
        setIsThreadHintPinnedOpen(false);
        if (getHoveredMessage() === hoverInstanceId) {
            setHoveredMessage(null);
        }
    }, [lnT, hoverInstanceId]);

    React.useEffect(() => {
        if (isHoverSuppressed) {
            clearHoverState();
        }
    }, [clearHoverState, isHoverSuppressed]);

    const isXBookmark = message?.id?.startsWith('x-');
    const isStarredMessage = Boolean(message?.isStarred);
    const effectiveSelectedMessagesMap = selectionMapOverride || selectedMessagesMap || {};
    const effectiveShowMultiSelect =
        typeof showMultiSelectOverride === 'boolean' ? showMultiSelectOverride : showMultiSelect;
    const isSelectionMode = Object.keys(effectiveSelectedMessagesMap).length > 0 || effectiveShowMultiSelect;
    const threadHintReplyCount = Number(threadHintCounts?.threadReplyCount ?? message?.threadReplyCount ?? 0);
    const hasExplicitThreadHintCounts = Boolean(threadHintCounts);
    const chatUpdatedAtTs = toSafeTime(message?.threadChatUpdatedAt);
    const threadUpdatedAtTs = toSafeTime(message?.threadMessageUpdatedAt);
    const activityUpdatedAtTs = toSafeTime(message?.threadActivityUpdatedAt);
    const hasChatActivity = chatUpdatedAtTs > 0;
    const hasThreadMessageActivity = threadUpdatedAtTs > 0;
    const hasBothThreadModes = hasChatActivity && hasThreadMessageActivity;
    const preferredThreadOpenMode =
        hasChatActivity && !hasThreadMessageActivity
            ? 'CHAT'
            : !hasChatActivity && hasThreadMessageActivity
              ? 'THREAD'
              : threadUpdatedAtTs > chatUpdatedAtTs
                ? 'THREAD'
                : 'CHAT';
    const computedThreadHintActionLabel =
        preferredThreadOpenMode === 'THREAD' ? lnT('folders:openThread') : lnT('messages:openChat');
    const hasThreadActivity =
        activityUpdatedAtTs > 0 ||
        hasChatActivity ||
        hasThreadMessageActivity ||
        (hasExplicitThreadHintCounts && threadHintReplyCount > 0);
    const usesCustomThreadHint = Boolean(threadHintCounts || onThreadHintClick);
    const canSupportThreadHint =
        Boolean(showThreadHint) &&
        (usesCustomThreadHint || (!message?.parentMessageId && isServerBackedMessageId(message?.id)));
    const canShowThreadHint = canSupportThreadHint && hasThreadActivity;
    const hasAttachedArtifactContent = hasArtifactContent(message);
    const artifactTypes = React.useMemo(() => getArtifactTypes(message), [lnT, message]);
    const attachedArtifactGroups = React.useMemo(
        () => sharedArtifactBadges || Array.from((Array.isArray(message?.artifacts) ? message.artifacts : []).reduce((groups, artifact) => {
            const rawType = String(artifact?.type || 'other').toLowerCase();
            const type = rawType === 'repair' ? 'tutorial' : rawType;
            if (!String(artifact?.title || '').trim()) return groups;
            groups.set(type, (groups.get(type) || 0) + 1);
            return groups;
        }, new Map()).entries()).map(([type, count]) => ({ type, count })),
        [lnT, message?.artifacts, sharedArtifactBadges]
    );
    const visibleMentionEntities = (message?.entities || []).filter(isMentionEntity);
    const canShowEntityTagsInMeta =
        layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.tags !== false;
    const [localBody, setLocalBody] = useState('');
    const [isEditingLocalBody, setIsEditingLocalBody] = useState(false);
    const noteSurfaceBackground = isStarredMessage ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.2)';
    const noteSurfaceBorder = isStarredMessage ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)';
    const noteTextColor = isStarredMessage ? 'rgba(35, 39, 42, 0.72)' : 'rgba(255, 255, 255, 0.78)';
    const noteEmptyTextColor = isStarredMessage ? 'rgba(35, 39, 42, 0.45)' : 'rgba(255, 255, 255, 0.45)';
    const noteMetaColor = isStarredMessage ? PRIMARY_COLOR_4 : DETAIL_TEXT;
    const normalizedMessageBody = React.useMemo(
        () => normalizeMarkdownForStorage(String(message?.body || '')),
        [lnT, message?.body]
    );
    const normalizedLocalBody = React.useMemo(() => normalizeMarkdownForStorage(String(localBody || '')), [lnT, localBody]);
    const bodyPlainTextForSearch = React.useMemo(
        () => (searchHighlightTerm ? stripMarkdownForPlainText(normalizedMessageBody) : ''),
        [lnT, normalizedMessageBody, searchHighlightTerm]
    );
    const bodySearchMatchCount = React.useMemo(
        () => (searchHighlightTerm ? countSearchMatches(bodyPlainTextForSearch, searchHighlightTerm) : 0),
        [lnT, bodyPlainTextForSearch, searchHighlightTerm]
    );
    const bodySearchMatchExcerpt = React.useMemo(() => {
        if (!searchHighlightTerm || !bodyPlainTextForSearch) {
            return null;
        }

        return buildSearchMatchPreviewExcerpt(bodyPlainTextForSearch, searchHighlightTerm, {
            maxLength: SEARCH_MATCH_EXCERPT_LENGTH,
            visibleWindow: SEARCH_MATCH_VISIBLE_WINDOW
        });
    }, [lnT, bodyPlainTextForSearch, searchHighlightTerm]);
    const shouldRenderLocalBodyAsPlainText = React.useMemo(
        () => shouldRenderMarkdownAsPlainText(normalizedLocalBody),
        [lnT, normalizedLocalBody]
    );
    const shouldRenderMessageBodyAsPlainText = React.useMemo(
        () => shouldRenderMarkdownAsPlainText(normalizedMessageBody),
        [lnT, normalizedMessageBody]
    );
    const threadAttachTargetLabel = React.useMemo(() => {
        const title = String(message?.title || '').trim();
        if (title) {
            return title;
        }

        const body = String(message?.body || '').replace(/\s+/g, ' ').trim();
        if (body) {
            return body.slice(0, 80);
        }

        return 'this message';
    }, [lnT, message?.body, message?.title]);
    const highlightColor = message?.metadata?.highlight?.color;
    const compactNoteMarkdownOptions = React.useMemo(
        () =>
            createMarkdownToJsxOptions({
                surface: isStarredMessage ? 'light' : 'dark',
                linkColor: isStarredMessage ? '#1f6fd6' : '#5ca2ff',
                textColor: noteTextColor,
                quoteColor: noteMetaColor,
                highlightColor
            }),
        [lnT, isStarredMessage, noteMetaColor, noteTextColor, highlightColor]
    );
    const targetParentId = String(message?.id || '').trim();
    const targetThreadRootId = String(message?.threadRootMessageId || message?.id || '').trim();
    const canAcceptThreadDrops =
        access.canManage && !disableThreadAttachTarget &&
        !isGridCardLayout(layoutMode) &&
        isServerBackedMessageId(targetParentId) &&
        isServerBackedMessageId(targetThreadRootId);
    const buildReminderNotificationId = React.useCallback(
        prefix => `${prefix}-${message.id}-${Date.now()}`,
        [lnT, message.id]
    );

    const broadcastMessageUpdated = React.useCallback(updatedMessage => {
        if (!updatedMessage?.id || typeof window === 'undefined') {
            return;
        }

        window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: updatedMessage } }));
    }, [lnT]);

    const formatReminderTimestamp = React.useCallback(value => {
        const reminderDate = value ? new Date(value) : null;
        if (!reminderDate || Number.isNaN(reminderDate.getTime())) {
            return '';
        }

        return new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(reminderDate);
    }, [lnT]);

    const normalizeReminderDeliveryType = React.useCallback(value => {
        const normalizedValue = String(value || '').trim().toLowerCase();
        return normalizedValue === 'email' ? 'EMAIL' : 'PUSH';
    }, [lnT]);

    const formatReminderDeliveryTypeLabel = React.useCallback(value => {
        return normalizeReminderDeliveryType(value) === 'EMAIL' ? 'email' : 'notification';
    }, [lnT, normalizeReminderDeliveryType]);

    const openReminderPicker = React.useCallback(
        event => {
            event.preventDefault();
            event.stopPropagation();

            if (!message?.id || (!effectiveReminderTimestamp && !reminderNeedsReview)) {
                return;
            }

            setShowReminderPicker(true);
        },
        [lnT, effectiveReminderTimestamp, reminderNeedsReview, message?.id]
    );

    const handleReminderMetaKeyDown = React.useCallback(
        event => {
            if (event.key !== 'Enter' && event.key !== ' ') {
                return;
            }

            openReminderPicker(event);
        },
        [lnT, openReminderPicker]
    );

    const submitReminder = React.useCallback(
        async ({ reminderAt, alerts, reminderMessage }) => {
            try {
                const response = await setMessageDueMutation({
                    variables: {
                        input: {
                            messageId: message.id,
                            dueAt: reminderAt.toISOString(),
                            dueTimezone: getReminderDueTimezone(),
                            dueKind: message.event ? 'event' : 'reminder',
                            alerts,
                            reminderMessage,
                            installationId: REMINDER_WEB_INSTALLATION_ID
                        }
                    }
                });

                const updatedMessage = response?.data?.setMessageDue;
                if (!updatedMessage) {
                    return;
                }

                broadcastMessageUpdated(updatedMessage);
                setShowReminderPicker(false);
                showDefaultNotification(
                    buildReminderNotificationId('set-reminder'),
                    `Reminder set for ${formatReminderTimestamp(updatedMessage.reminderAt)} via ${formatReminderDeliveryTypeLabel(updatedMessage.reminderDeliveryType)}`
                );
                customAnalytics.reminder_action({
                    'action': 'Set',
                    'delivery_type': alerts?.[0]?.deliveryType,
                    'lead_hours': Math.round((reminderAt.getTime() - Date.now()) / 3600000),
                    'has_note': Boolean(String(reminderMessage || '').trim()),
                    'alert_count': alerts?.length || 0,
                    'source': 'MessageCard'
                });
            } catch (error) {
                const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToSaveReminder');
                showDefaultNotification(buildReminderNotificationId('set-reminder-error'), errorMessage);
            }
        },
        [lnT,
            broadcastMessageUpdated,
            buildReminderNotificationId,
            formatReminderDeliveryTypeLabel,
            formatReminderTimestamp,
            message.id,
            setMessageDueMutation,
            showDefaultNotification
        ]
    );

    const clearGlobalMultiSelect = useCallback(() => {
        if (typeof setMessagesToSend === 'function') {
            setMessagesToSend([]);
        }
        if (typeof setSelectedMessagesMap === 'function') {
            setSelectedMessagesMap({});
        }
        if (typeof setShowMultiSelect === 'function') {
            setShowMultiSelect(false);
        }
    }, [lnT, setMessagesToSend, setSelectedMessagesMap, setShowMultiSelect]);

    const resolveDroppedMessages = useCallback(
        droppedItem => {
            const candidateMessages =
                Array.isArray(messagesToSend) && messagesToSend.length > 0
                    ? messagesToSend
                    : [droppedItem?.message].filter(Boolean);
            const seenMessageIds = new Set();

            return candidateMessages.filter(candidateMessage => {
                const candidateId = String(candidateMessage?.id || '');
                if (!candidateId || !isServerBackedMessageId(candidateId) || seenMessageIds.has(candidateId)) {
                    return false;
                }

                const candidateThreadRootId = String(
                    candidateMessage?.threadRootMessageId || candidateMessage?.id || ''
                ).trim();

                if (candidateId === targetParentId || candidateThreadRootId === targetThreadRootId) {
                    return false;
                }

                seenMessageIds.add(candidateId);
                return true;
            });
        },
        [lnT, messagesToSend, targetParentId, targetThreadRootId]
    );

    const attachDroppedMessagesToThread = useCallback(
        async droppedMessages => {
            const eligibleMessages = droppedMessages.filter(droppedMessage => droppedMessage?.id);

            if (!targetParentId || !targetThreadRootId || !eligibleMessages.length) {
                return;
            }

            const rootFolder = message?.folder || selectedFolder || null;
            const resolvedEligibleMessages = eligibleMessages.map(droppedMessage => {
                const hasMediaPayload = Boolean(
                    droppedMessage?.image ||
                        droppedMessage?.file ||
                        (Array.isArray(droppedMessage?.urlMeta) && droppedMessage.urlMeta.length > 0)
                );

                if (hasMediaPayload) {
                    return droppedMessage;
                }

                const droppedMessageId = String(droppedMessage?.id || '');
                if (!isServerBackedMessageId(droppedMessageId)) {
                    return droppedMessage;
                }

                try {
                    const cachedMessage = apolloClient.readQuery({
                        query: QUERY.GET_MESSAGE,
                        variables: {
                            data: {
                                id: droppedMessageId
                            }
                        }
                    })?.getMessage;

                    if (cachedMessage) {
                        return {
                            ...cachedMessage,
                            ...droppedMessage
                        };
                    }
                } catch (error) {}

                return droppedMessage;
            });

            try {
                await attachMessagesToThreadMutation({
                    variables: {
                        data: {
                            parentMessageId: targetParentId,
                            messageIds: resolvedEligibleMessages.map(droppedMessage => droppedMessage.id),
                            laneKind: 'PERSONAL'
                        }
                    }
                });

                CacheManager.removeMessages(apolloClient, resolvedEligibleMessages, {
                    skipNormalizedEviction: true
                });

                resolvedEligibleMessages.forEach(droppedMessage => {
                    CacheManager.updateSingleMessage(apolloClient, droppedMessage.id, {
                        folder: rootFolder,
                        parentMessageId: targetParentId,
                        threadRootMessageId: targetThreadRootId,
                        laneKind: 'PERSONAL'
                    });

                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                            new CustomEvent('messageUpdated', {
                                detail: {
                                    message: {
                                        ...droppedMessage,
                                        folder: rootFolder,
                                        parentMessageId: targetParentId,
                                        threadRootMessageId: targetThreadRootId,
                                        laneKind: 'PERSONAL',
                                        __threadAttachTransition: true
                                    }
                                }
                            })
                        );
                    }
                });

                if (typeof window !== 'undefined') {
                    const movedMessagesBySourceFolder = resolvedEligibleMessages.reduce((map, droppedMessage) => {
                        const sourceFolderId = String(
                            droppedMessage?.folder?.id || droppedMessage?.folder?._id || droppedMessage?.folder || ''
                        );

                        if (!sourceFolderId) {
                            return map;
                        }

                        if (!map.has(sourceFolderId)) {
                            map.set(sourceFolderId, []);
                        }

                        map.get(sourceFolderId).push({
                            ...droppedMessage,
                            folder: rootFolder,
                            parentMessageId: targetParentId,
                            threadRootMessageId: targetThreadRootId,
                            laneKind: 'PERSONAL'
                        });

                        return map;
                    }, new Map());

                    movedMessagesBySourceFolder.forEach((sourceMessages, sourceFolderId) => {
                        window.dispatchEvent(
                            new CustomEvent('messagesMoved', {
                                detail: {
                                    messageIds: sourceMessages.map(sourceMessage => sourceMessage.id),
                                    fromFolderId: sourceFolderId,
                                    toFolderId: rootFolder?.id,
                                    toFolder: rootFolder,
                                    movedMessages: sourceMessages,
                                    threadAttachTargetRootId: targetThreadRootId
                                }
                            })
                        );
                    });
                }

                clearGlobalMultiSelect();
            } catch (error) {
                passingErrors(error);
            }
        },
        [lnT,
            apolloClient,
            attachMessagesToThreadMutation,
            clearGlobalMultiSelect,
            message?.folder,
            passingErrors,
            selectedFolder,
            targetParentId,
            targetThreadRootId
        ]
    );
    const pendingThreadAttachCount = pendingThreadAttachMessages.length;
    const handleThreadAttachConfirmClose = useCallback(
        shouldAttach => {
            const droppedMessages = pendingThreadAttachMessages;
            setPendingThreadAttachMessages([]);

            if (shouldAttach && droppedMessages.length > 0) {
                void attachDroppedMessagesToThread(droppedMessages);
            }
        },
        [lnT, attachDroppedMessagesToThread, pendingThreadAttachMessages]
    );

    // Initialize/Load local body from localStorage
    React.useEffect(() => {
        if (isXBookmark) {
            const savedBody = localStorage.getItem(`luckynote_local_body_${message.id}`);
            if (savedBody) {
                setLocalBody(savedBody);
            }
        }
    }, [isXBookmark, message.id]);

    const handleLocalBodySave = (newBody) => {
        setLocalBody(newBody);
        localStorage.setItem(`luckynote_local_body_${message.id}`, newBody);
        
        // Dispatch custom event for cross-component synchronization
        window.dispatchEvent(new CustomEvent('localNoteUpdated', {
            detail: {
                messageId: message.id,
                localBody: newBody
            }
        }));
    };

    // Listen for local note updates from other components (e.g., Preview Sidebar)
    React.useEffect(() => {
        if (!isXBookmark) return;

        const handleUpdate = (event) => {
            const { messageId, localBody: updatedBody } = event.detail || {};
            if (messageId === message.id) {
                setLocalBody(updatedBody);
            }
        };

        window.addEventListener('localNoteUpdated', handleUpdate);
        return () => window.removeEventListener('localNoteUpdated', handleUpdate);
    }, [isXBookmark, message.id]);

    // Update local hover state based on global state
    React.useEffect(() => {
        const listener = (previousId, currentId) => {
            if (currentId === hoverInstanceId) {
                setHoveringContainer(true);
            } else if (previousId === hoverInstanceId) {
                setHoveringContainer(false);
            }
        };
        const removeListener = addHoverListener(listener);
        // Initialize hover state on mount
        if (getHoveredMessage() === hoverInstanceId) {
            setHoveringContainer(true);
        }
        return () => {
            removeListener();
            if (getHoveredMessage() === hoverInstanceId) {
                setHoveredMessage(null);
            }
        };
    }, [hoverInstanceId]);

    // Clean up hover state when component unmounts or when messages are selected
    React.useEffect(() => {
        if (Object.keys(effectiveSelectedMessagesMap).length > 0 && getHoveredMessage() === hoverInstanceId) {
            setHoveredMessage(null);
        }
    }, [effectiveSelectedMessagesMap, hoverInstanceId]);

    React.useEffect(() => {
        if (isSelectionMode) {
            setHoveringMessage(false);
            setHoveringSelectTarget(false);
            if (getHoveredMessage() === hoverInstanceId) {
                setHoveredMessage(null);
            }
        }
    }, [hoverInstanceId, isSelectionMode]);

    React.useEffect(() => {
        if (!hoveringContainer && !hoveringMessage && !hoveringSelectTarget) {
            return undefined;
        }

        const syncActualHoverState = () => {
            const node = messageRef.current;
            if (!node) {
                clearHoverState();
                return;
            }

            if (!node.matches(':hover')) {
                clearHoverState();
            }
        };

        const handleDocumentPointerMove = () => {
            syncActualHoverState();
        };

        const handleDocumentMouseLeave = () => {
            clearHoverState();
        };

        const handleWindowBlur = () => {
            clearHoverState();
        };

        document.addEventListener('pointermove', handleDocumentPointerMove, true);
        document.addEventListener('mouseleave', handleDocumentMouseLeave);
        window.addEventListener('blur', handleWindowBlur);

        return () => {
            document.removeEventListener('pointermove', handleDocumentPointerMove, true);
            document.removeEventListener('mouseleave', handleDocumentMouseLeave);
            window.removeEventListener('blur', handleWindowBlur);
        };
    }, [clearHoverState, hoveringContainer, hoveringMessage, hoveringSelectTarget]);

    const [{ isOverThreadDropTarget, canDropOnThreadTarget }, threadDrop] = useDrop({
        accept: [ItemTypes.MESSAGE],
        canDrop: droppedItem => canAcceptThreadDrops && resolveDroppedMessages(droppedItem).length > 0,
        drop: droppedItem => {
            const droppedMessages = resolveDroppedMessages(droppedItem);
            if (!droppedMessages.length) {
                return;
            }

            setPendingThreadAttachMessages(droppedMessages);
        },
        collect: monitor => ({
            isOverThreadDropTarget: monitor.isOver({ shallow: true }),
            canDropOnThreadTarget: monitor.canDrop()
        })
    });

    const [{ isDragging }, drag, preview] = useDrag({
        item: { name, type: ItemTypes.MESSAGE, message },
        options: {
            dropEffect: 'move'
        },
        end: (item, monitor) => {
            const dropResult = monitor.getDropResult();
            // console.log(dropResult);
            // console.log(item);
            // alert(`You dropped ${message.id} into ${dropResult.name}!`);

            // just to refresh a message when getting dropped
            if (item && dropResult) {
                // console.log('do you go in?', item, dropResult, message)
                getMessage({
                    variables: {
                        data: {
                            id: message.id
                        }
                    }
                });
            }
        },
        collect: monitor => ({
            isDragging: monitor.isDragging()
        }),
        canDrag: (monitor) => {
            if (!access.canManage) return false;
            // Check if drag started from audio scrubber
            const dragSource = document.elementFromPoint(monitor.getClientOffset()?.x || 0, monitor.getClientOffset()?.y || 0);
            if (dragSource?.tagName === 'INPUT' && dragSource?.type === 'range' && dragSource?.hasAttribute('data-prevent-message-interaction')) {
                return false;
            }

            // Video's native controls (including the seek scrubber) live in the
            // browser's own shadow DOM, so elementFromPoint just returns the
            // <video> element itself rather than a distinguishable child. Keep
            // the control strip interactive, but allow dragging from the rest
            // of the video preview.
            if (dragSource?.tagName === 'VIDEO') {
                const clientOffset = monitor.getClientOffset();
                const videoRect = dragSource.getBoundingClientRect();
                const clickedControlBar = clientOffset?.y >= videoRect.bottom - 40;
                if (clickedControlBar) {
                    return false;
                }
            }

            if (holdingKeys && keys.Meta) {
                return false;
            }
            return true;
        }
    });

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    const opacity = isDragging ? 0.4 : 1;
    const messageUrlMeta = Array.isArray(message?.urlMeta) ? message.urlMeta : [];

    const renderImageContent = () => {
        const normalizedImage =
            typeof message.image === 'string'
                ? { url: message.image, cdn_url: message.image }
                : message.image;
        let margin = 0;
        if (message.file) {
            margin = MARGIN;
        }
        if (messageUrlMeta.length > 0) {
            messageUrlMeta.map((metaData, index) => {
                if (messageUrlMeta[index]) {
                    if (metaData.title || metaData.description || metaData.image) {
                        margin = MARGIN;
                    }
                }
            });
        }
        if (message.title && message.title.length > 0) {
            margin = MARGIN;
        }
        if (message.body && message.body.length > 0) {
            margin = MARGIN;
        }
        if (normalizedImage) {
            const gridCard = isGridCardLayout(layoutMode);

            if (!smallImage) {
                return (
                    <div style={{ marginBottom: margin }}>
                        <ImageSection
                            readOnly={forceReadOnly}
                            url={normalizedImage.cdn_url || normalizedImage.url}
                            image={normalizedImage}
                            messageId={message.id}
                            isUploading={isUploading}
                            uploadProgress={uploadProgress}
                            uploadStatus={uploadStatus}
                            onCancelUpload={handleCancelUpload}
                            cover={gridCard}
                            onClick={() => {
                                if (typeof onOpenVideoWorkspace === 'function') {
                                    onOpenVideoWorkspace(message, {
                                        type: 'image',
                                        image: normalizedImage,
                                        url: normalizedImage.thumbnail?.xl ||
                                            normalizedImage.thumbnail?.lg ||
                                            normalizedImage.thumbnail?.original ||
                                            normalizedImage.cdn_url ||
                                            normalizedImage.url,
                                        originalUrl: normalizedImage.thumbnail?.original ||
                                            normalizedImage.url ||
                                            normalizedImage.cdn_url
                                    });
                                    return;
                                }

                                imagePreview(message);
                            }}
                        ></ImageSection>
                    </div>
                );
            } else {
                return (
                    <div
                        style={{
                            marginBottom: margin,
                            backgroundColor: gridCard ? 'transparent' : NOTE_BACKGROUND,
                            borderRadius: 8,
                            maxHeight: 250,
                            overflow: 'hidden',
                            display: 'flex'
                        }}
                    >
                        <ImageSection
                            readOnly={forceReadOnly}
                            url={normalizedImage.cdn_url || normalizedImage.url}
                            image={normalizedImage}
                            messageId={message.id}
                            isUploading={isUploading}
                            uploadProgress={uploadProgress}
                            uploadStatus={uploadStatus}
                            onCancelUpload={handleCancelUpload}
                            cover={gridCard}
                            onClick={() => {
                                if (typeof onOpenVideoWorkspace === 'function') {
                                    onOpenVideoWorkspace(message, {
                                        type: 'image',
                                        image: normalizedImage,
                                        url: normalizedImage.thumbnail?.xl ||
                                            normalizedImage.thumbnail?.lg ||
                                            normalizedImage.thumbnail?.original ||
                                            normalizedImage.cdn_url ||
                                            normalizedImage.url,
                                        originalUrl: normalizedImage.thumbnail?.original ||
                                            normalizedImage.url ||
                                            normalizedImage.cdn_url
                                    });
                                    return;
                                }

                                imagePreview(message);
                            }}
                        ></ImageSection>
                    </div>
                );
            }
        }
    };

    const handleSelectionModeInteractiveClickCapture = event => {
        if (!isSelectionMode || !multipleSelect) {
            return;
        }

        const interactiveTarget = event.target?.closest?.(
            'a, button, input, textarea, select, img, video, svg, canvas, [role="button"], [data-message-interactive="true"], [data-message-media-interactive="true"]'
        );

        if (!interactiveTarget) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        multipleSelect(event);
    };

    const renderAWSContent = () => {
        let margin = 0;
        if (messageUrlMeta.length > 0) {
            messageUrlMeta.map((metaData, index) => {
                if (messageUrlMeta[index]) {
                    if (metaData.title || metaData.description || metaData.image) {
                        margin = MARGIN;
                    }
                }
            });
        }
        if (message.title && message.title.length > 0) {
            margin = MARGIN;
        }
        if (message.body && message.body.length > 0) {
            margin = MARGIN;
        }

        if (message.file) {
            const fileType = message.file.type || '';
            if (
                isFileTypeImage(fileType) ||
                fileType.includes('application')
                // message.file.type === 'image/png' ||
                // message.file.type === 'image/jpg' ||
                // message.file.type === 'image/jpeg' ||
                // message.file.type === 'image/svg' ||
                // message.file.type === 'image/svg+xml' ||
                // message.file.type === 'image/heic' ||
                // message.file.type === 'application/zip' ||
                // message.file.type === 'application/pdf'
            ) {
                return (
                    <div style={{ marginBottom: margin }}>
                        <FileSection
                            readOnly={forceReadOnly}
                            file={message.file}
                            isUploading={isUploading}
                            uploadProgress={uploadProgress}
                            uploadStatus={uploadStatus}
                            onCancelUpload={handleCancelUpload}
                            messageId={message?.id}
                            isStarred={Boolean(message?.isStarred)}
                            hideActions={layoutMode === 'masonry'}
                            forceStackPreviewLayout={layoutMode === 'masonry'}
                            showWorkspaceButton={layoutMode === 'masonry'}
                            onOpenVideoWorkspace={typeof onOpenVideoWorkspace === 'function'
                                ? videoSource => onOpenVideoWorkspace(message, videoSource) : undefined}
                        />
                    </div>
                );
            } else if (
                fileType.includes('audio')
                // message.file.type === 'audio/wav' ||
                // message.file.type === 'audio/aac' ||
                // message.file.type === 'audio/mpeg' ||
                // message.file.type === 'audio/mp3'
            ) {
                return (
                    <div style={{ marginBottom: margin }}>
                        <AudioSection
                            file={message.file}
                            message={message}
                            showTitle={true}
                            showTranscriptionIntro={true}
                            isUploading={isUploading}
                            uploadProgress={uploadProgress}
                            uploadStatus={uploadStatus}
                            onCancelUpload={handleCancelUpload}
                        ></AudioSection>
                    </div>
                );
            } else if (fileType.includes('video')) {
                return (
                    <div style={{ marginBottom: margin }}>
                        <VideoSection 
                            messageId={message.id}
                            url={message.file.cdn_url || message.file.url} 
                            file={message.file}
                            isUploading={isUploading}
                            uploadProgress={uploadProgress}
                            uploadStatus={uploadStatus}
                            onCancelUpload={handleCancelUpload}
                            autoplayOnHover={!isUploading}
                            openInWorkspaceOnly={openVideoInWorkspaceOnly}
                            onOpenVideoWorkspace={typeof onOpenVideoWorkspace === 'function'
                                ? videoSource => onOpenVideoWorkspace(message, videoSource) : undefined}
                        ></VideoSection>
                    </div>
                );
            } else {
                // just displaying it as an ordinary file
                return (
                    <div style={{ marginBottom: margin }}>
                        <FileSection
                            readOnly={forceReadOnly}
                            file={message.file}
                            isUploading={isUploading}
                            uploadProgress={uploadProgress}
                            uploadStatus={uploadStatus}
                            onCancelUpload={handleCancelUpload}
                            messageId={message?.id}
                            isStarred={Boolean(message?.isStarred)}
                            hideActions={layoutMode === 'masonry'}
                            forceStackPreviewLayout={layoutMode === 'masonry'}
                            showWorkspaceButton={layoutMode === 'masonry'}
                            onOpenVideoWorkspace={typeof onOpenVideoWorkspace === 'function'
                                ? videoSource => onOpenVideoWorkspace(message, videoSource) : undefined}
                        />
                    </div>
                );
            }
        }
        return null;
    };

    const renderUrlMeta = () => {
        let margin = 0;
        if (message.title && message.title.length > 0) {
            margin = MARGIN / 2;
        }
        if (message.body && message.body.length > 0) {
            margin = MARGIN / 2;
        }
        if (messageUrlMeta.length > 0) {
            if (messageUrlMeta.length > 1) {
                margin = MARGIN / 2
            }
            return messageUrlMeta.map((metaData, index) => {
                if (messageUrlMeta[index]) {
                    const showUrlMetaTitle =
                        layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.urlMetaTitle !== false;
                    const showUrlMetaDescription =
                        layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.urlMetaDescription !== false;

                    return (
                        <div
                            key={`url-meta-${message.id}-${index}-${showUrlMetaTitle ? 'title-on' : 'title-off'}-${showUrlMetaDescription ? 'desc-on' : 'desc-off'}-${layoutMode}`}
                            style={{ marginBottom: margin, display: 'flex', overflow: 'hidden', width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                        >
                            <URLPreviewSection
                                readOnly={forceReadOnly}
                                key={`url-preview-${message.id}-${index}-${showUrlMetaTitle ? 'title-on' : 'title-off'}-${showUrlMetaDescription ? 'desc-on' : 'desc-off'}-${layoutMode}`}
                                index={index}
                                metaData={metaData}
                                message={message}
                                layoutMode={layoutMode}
                                stackLayoutMaxWidth={getUrlPreviewStackLayoutMaxWidth(layoutMode, metaData)}
                                showTitle={showUrlMetaTitle}
                                showDescription={showUrlMetaDescription}
                                searchHighlightTerm={searchHighlightTerm}
                                autoplayOnHover={true}
                                onOpenVideoWorkspace={typeof onOpenVideoWorkspace === 'function'
                                ? videoSource => onOpenVideoWorkspace(message, videoSource) : undefined}
                                onCreateArticleAudio={onCreateArticleAudio}
                                isCreatingArticleAudio={isCreatingArticleAudio}
                            />
                        </div>
                    );
                }
                return null;
            }).filter(Boolean); // Remove null values
        }

        // No urlMeta — show enriched entity preview cards for entities with an image
        const enrichedEntities = (message.entities || []).filter(e => e?.enrichment?.image);
        if (enrichedEntities.length > 0) {
            const hasContentAbove = Boolean(
                message.image ||
                message.file ||
                message.replyMessageId
            );
            return enrichedEntities.map((entity, index) => (
                <EnrichmentCard
                    key={`entity-enrichment-${message.id}-${index}`}
                    entity={entity}
                    margin={margin}
                    layoutMode={layoutMode}
                    hasContentAbove={index > 0 || hasContentAbove}
                />
            ));
        }

        return null;
    };

    const handleMentionClick = (mention, e) => {
        e.stopPropagation();
        const username = mention.substring(1); // Remove @ symbol
        setSelectedUsername(username);
        setShowSocialModal(true);
        customAnalytics.message_action({
            'action': "Open",
            "type": "Mention",
            "username": username
        }, message);
    };

    const handleTitleHashtagClick = (hashtag, e) => {
        e.stopPropagation();
        const tag = hashtag.substring(1);
        setSearchTerm(hashtag);
        setSearchActive(true);
        customAnalytics.message_action({
            'action': "Open",
            "type": "Hashtag",
            "tag": tag,
            "search_term": hashtag,
            "screen": "SearchMessagesScreen"
        }, message);
    };

    const openUrlInBrowser = (url) => {
        let validUrl = url.trim();
        if (!/^https?:\/\//i.test(validUrl)) {
            validUrl = `https://${validUrl}`;
        }

        openExternalUrl(validUrl);
    };

    const handleSocialNetworkSelect = (networkId, username) => {
        let url;
        switch (networkId) {
            case 'x':
                url = `https://x.com/${username}`;
                break;
            case 'instagram':
                url = `https://instagram.com/${username}`;
                break;
            case 'facebook':
                url = `https://facebook.com/${username}`;
                break;
            case 'linkedin':
                url = `https://linkedin.com/in/${username}`;
                break;
            case 'github':
                url = `https://github.com/${username}`;
                break;
            case 'medium':
                url = `https://medium.com/@${username}`;
                break;
            case 'dev':
                url = `https://dev.to/${username}`;
                break;
            case 'youtube':
                url = `https://youtube.com/@${username}`;
                break;
            case 'tiktok':
                url = `https://tiktok.com/@${username}`;
                break;
            case 'twitch':
                url = `https://twitch.tv/${username}`;
                break;
            case 'behance':
                url = `https://behance.net/${username}`;
                break;
            case 'telegram':
                url = `https://t.me/${username}`;
                break;
        }
        if (url) {
            customAnalytics.message_action({
                'action': "Open",
                "type": "Social",
                "network": networkId,
                "username": username
            }, message);
            
            openUrlInBrowser(url);
        }
    };

    const titleMarkdownOptions = React.useMemo(() => {
        const linkColor = isStarredMessage ? '#1f6fd6' : '#5ca2ff';
        const baseOptions = createMarkdownToJsxOptions({
            surface: isStarredMessage ? 'light' : 'dark',
            linkColor,
            textColor: noteTextColor,
            quoteColor: noteMetaColor
        });

        const InteractiveTitleMarkdownAnchor = ({ children, href, ...props }) => {
    const {t: lnT} = useTranslation();

            const validProps = getValidMarkdownAnchorProps(props);
            const safeHref = typeof href === 'string' ? href : '#';
            const [showCopied, setShowCopied] = useState(false);
            const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
            const anchorRef = useRef(null);

            if (safeHref.startsWith('copy-email:') || safeHref.startsWith('copy-phone:')) {
                const isPhone = safeHref.startsWith('copy-phone:');
                const value = decodeURIComponent(safeHref.slice(safeHref.indexOf(':') + 1));
                const textToCopy = isPhone ? value.replace(/\s/g, '') : value;

                const handleCopyClick = async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                        await navigator.clipboard.writeText(textToCopy);
                        const rect = anchorRef.current?.getBoundingClientRect();
                        if (rect) {
                            setTooltipPos({
                                top: Math.max(10, rect.top - 30),
                                left: rect.left + rect.width / 2
                            });
                        }
                        setShowCopied(true);
                        customAnalytics.message_action({
                            action: 'Copy',
                            type: isPhone ? 'PhoneNumber' : 'Email'
                        }, message);
                        setTimeout(() => setShowCopied(false), 2000);
                    } catch (err) {
                        console.error('Failed to copy:', err);
                    }
                };

                return (
                    <>
                        <span
                            ref={anchorRef}
                            data-message-interactive="true"
                            onClick={handleCopyClick}
                            style={{
                                color: '#2b88d9',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            {children}
                        </span>
                        {showCopied && ReactDOM.createPortal(
                            <div style={{
                                position: 'fixed',
                                top: `${tooltipPos.top}px`,
                                left: `${tooltipPos.left}px`,
                                transform: 'translateX(-50%)',
                                background: 'rgba(0, 0, 0, 0.75)',
                                color: '#fff',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                zIndex: 99999,
                                pointerEvents: 'none'
                            }}>{lnT('messages:copied')}</div>,
                            document.body
                        )}
                    </>
                );
            }

            const handleClick = event => {
                event.preventDefault();
                event.stopPropagation();

                if (safeHref.startsWith('mention:')) {
                    const username = decodeURIComponent(safeHref.slice('mention:'.length));
                    setSelectedUsername(username);
                    setShowSocialModal(true);
                    customAnalytics.message_action({
                        action: 'Open',
                        type: 'Mention',
                        username
                    }, message);
                    return;
                }

                if (safeHref.startsWith('hashtag:')) {
                    const hashtag = `#${decodeURIComponent(safeHref.slice('hashtag:'.length))}`;
                    handleTitleHashtagClick(hashtag, event);
                    return;
                }

                if (safeHref.startsWith('stock:')) {
                    const symbol = decodeURIComponent(safeHref.slice('stock:'.length));
                    setSelectedStockTicker(symbol);
                    setShowStockTickerModal(true);
                    customAnalytics.message_action({
                        action: 'Open',
                        type: 'StockTicker'
                    }, message);
                    return;
                }

                customAnalytics.message_action({
                    action: 'Open',
                    type: 'Link'
                }, message);
                openUrlInBrowser(safeHref);
            };

            return (
                <a
                    {...validProps}
                    href={safeHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-message-interactive="true"
                    onClick={handleClick}
                    style={{
                        color: linkColor,
                        textDecoration: 'underline',
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        maxWidth: '100%',
                        display: 'inline-block'
                    }}
                >
                    {React.Children.map(children, child => typeof child === 'string' ? decodeDisplayText(child) : child)}
                </a>
            );
        };

        return {
            ...baseOptions,
            overrides: {
                ...baseOptions.overrides,
                a: {
                    component: InteractiveTitleMarkdownAnchor
                }
            }
        };
    }, [lnT,
        isStarredMessage,
        message,
        noteMetaColor,
        noteTextColor,
        openUrlInBrowser,
        handleTitleHashtagClick,
        setSelectedStockTicker,
        setSelectedUsername,
        setShowSocialModal,
        setShowStockTickerModal
    ]);

    const renderTitleContent = () => {
        if (isXBookmark) return null;
        let margin = 0;
        if (message.body && message.body.length > 0) {
            margin = MARGIN / 2;
        }

        const renderURLText = (text, hrefOverride = '', key) => {
            // Ensure text is a string, not a boolean or other type
            if (!text || typeof text !== 'string') {
                return <span>{String(text || '')}</span>;
            }
            
            // For href, clean the text: remove all newlines and trim.
            // For display, we use the original 'text' prop.
            const textForHref = text.replace(/[\n\r]+/g, ' ').trim();
            
            // Ensure we have a valid string after processing
            if (!textForHref) {
                return <span>{text}</span>;
            }
            
            const isActualUrl = /^(https?:\/\/|www\.)/i.test(textForHref);
            
            let fullUrl = String(hrefOverride || '').trim();
            if (!fullUrl) {
                fullUrl = textForHref;
                if (!isActualUrl && textForHref) { // If not starting with http/www, prepend https://
                    fullUrl = `https://${textForHref}`;
                }
                if (textForHref.startsWith('@http')) { // Handle @http case
                     fullUrl = textForHref.substring(1);
                }
            }

            // Ensure fullUrl is a valid string before using
            if (!fullUrl || typeof fullUrl !== 'string') {
                return <span>{text}</span>;
            }

            const handleLinkClick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                customAnalytics.message_action({
                    'action': "Open",
                    "type": "Link"
                }, message);
                openUrlInBrowser(fullUrl);
            };

            const normalizedDisplayText = text.replace(/\s+/g, ' ').trim();
            const expandedLinkKey = `${message?.id || 'message'}:${normalizedDisplayText}`;

            return (
                <ExpandableLinkText
                    key={key}
                    href={fullUrl}
                    text={decodeDisplayText(text)}
                    title={decodeDisplayText(fullUrl)}
                    onClick={handleLinkClick}
                    isCollapsible={true}
                    isExpanded={Boolean(expandedTitleLinks[expandedLinkKey])}
                    onToggle={() => {
                        setExpandedTitleLinks(previousState => ({
                            ...previousState,
                            [expandedLinkKey]: !previousState[expandedLinkKey]
                        }));
                    }}
                />
            );
        };

        const CopyableText = ({ text, type = "PhoneNumber" }) => {
    const {t: lnT} = useTranslation();

            const [showCopied, setShowCopied] = useState(false);
            const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
            const spanRef = useRef(null);
            const timeoutRef = useRef(null);

            // Cleanup timeout on unmount
            React.useEffect(() => {
                return () => {
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                };
            }, []);

            const handleCopy = async (e) => {
                e.stopPropagation();
                try {
                    // Clear any existing timeout
                    if (timeoutRef.current) {
                        clearTimeout(timeoutRef.current);
                    }
                    
                    // Remove spaces when copying to clipboard only for phone numbers
                    const textToCopy = type === "PhoneNumber" ? text.replace(/\s/g, '') : text;
                    await navigator.clipboard.writeText(textToCopy);
                    
                    // Calculate tooltip position synchronously before showing
                    let position = { top: 0, left: 0 };
                    if (spanRef.current) {
                        const rect = spanRef.current.getBoundingClientRect();
                        position = {
                            top: Math.max(10, rect.top - 30), // Position above the element, but at least 10px from top
                            left: rect.left + (rect.width / 2) // Center horizontally
                        };
                    }
                    
                    setTooltipPosition(position);
                    setShowCopied(true);
                    
                    customAnalytics.message_action({
                        'action': "Copy",
                        "type": type
                    }, message);
                    
                    // Keep tooltip visible for 2 seconds regardless of mouse movement
                    timeoutRef.current = setTimeout(() => {
                        setShowCopied(false);
                        timeoutRef.current = null;
                    }, 2000);
                } catch (error) {
                    console.error('Failed to copy text:', error);
                }
            };

            const tooltip = showCopied ? (
                <div
                    style={{
                        position: 'fixed',
                        top: `${tooltipPosition.top}px`,
                        left: `${tooltipPosition.left}px`,
                        transform: 'translateX(-50%)',
                        background: 'rgba(0, 0, 0, 0.75)',
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        zIndex: 99999,
                        pointerEvents: 'none'
                    }}
                >{lnT('messages:copied')}</div>
            ) : null;

            return (
                <>
                    <span
                        ref={spanRef}
                        data-message-interactive="true"
                        onClick={handleCopy}
                        style={{
                            position: 'relative',
                            display: 'inline-block',
                            color: type === "Mention" ? '#1da1f2' : 
                                   type === "Hashtag" ? '#ff7a00' : '#2b88d9',
                            cursor: 'pointer',
                            fontWeight: 600,
                        }}
                    >
                        {text}
                    </span>
                    {tooltip && ReactDOM.createPortal(tooltip, document.body)}
                </>
            );
        };

        const HashtagText = ({ text }) => {
            return (
                <span
                    data-message-interactive="true"
                    onClick={(e) => handleTitleHashtagClick(text, e)}
                    style={{
                        color: '#ff7a00',
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    {text}
                </span>
            );
        };

        const StockTickerText = ({ text }) => {
            return (
                <span
                    data-message-interactive="true"
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedStockTicker(text);
                        setShowStockTickerModal(true);
                        customAnalytics.message_action({
                            'action': "Open",
                            "type": "StockTicker"
                        }, message);
                    }}
                    style={{
                        color: '#00873c',
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    {text}
                </span>
            );
        };

        const processText = (text) => {
            const tokens = tokenizeInteractiveText(text);
            if (!tokens) {
                return null;
            }

            return tokens.map((token, index) => {
                const key = `${message?.id || 'message'}-title-${token.type}-${index}`;

                switch (token.type) {
                    case 'url':
                        return renderURLText(token.text, '', key);
                    case 'markdownLink':
                        return renderURLText(token.text, token.url, key);
                    case 'email':
                        return <CopyableText key={key} text={token.text} type="Email" />;
                    case 'phone':
                        return <CopyableText key={key} text={token.text} type="PhoneNumber" />;
                    case 'stockTicker':
                        return <StockTickerText key={key} text={token.text} />;
                    case 'mention':
                        return (
                            <span
                                key={key}
                                data-message-interactive="true"
                                onClick={(e) => handleMentionClick(token.text, e)}
                                style={{
                                    color: '#1da1f2',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                }}
                            >
                                {token.text}
                            </span>
                        );
                    case 'hashtag':
                        return <HashtagText key={key} text={token.text} />;
                    case 'protocolUrl':
                    case 'text':
                    default:
                        return renderHighlightedSearchText(token.text, searchHighlightTerm);
                }
            });
        };

        if (message.title) {
            const normalizedTitle = normalizeMarkdownForStorage(String(message.title || ''));
            const shouldRenderTitleAsMarkdown = hasMarkdownFormatting(normalizedTitle);
            const linkedTitleMarkdown = shouldRenderTitleAsMarkdown
                ? linkifyMarkdownTitle(normalizedTitle)
                : normalizedTitle;

            return (
                <div style={{ marginBottom: margin }}>
                    {shouldRenderTitleAsMarkdown ? (
                        <TitleMarkdownPreview>
                            {searchHighlightTerm ? (
                                highlightReactChildren(
                                    compiler(linkedTitleMarkdown, titleMarkdownOptions),
                                    searchHighlightTerm
                                )
                            ) : (
                                <Markdown options={titleMarkdownOptions}>{linkedTitleMarkdown}</Markdown>
                            )}
                        </TitleMarkdownPreview>
                    ) : (
                        <PreMessage>
                            {processText(message.title)}
                        </PreMessage>
                    )}
                </div>
            );
        }
    };

    const renderBodyContent = () => {
        if (isXBookmark) {
            return (
                <div
                    data-message-interactive="true"
                    style={{
                        color: localBody ? noteTextColor : noteEmptyTextColor,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: noteSurfaceBackground,
                        borderRadius: 8,
                        padding: 8,
                        minWidth: 100,
                        cursor: 'text',
                        border: isEditingLocalBody
                            ? `1px solid ${PRIMARY_COLOR_6}`
                            : `1px solid ${noteSurfaceBorder}`
                    }}
                    onClick={(e) => {
                        if (e.target instanceof Element && e.target.closest('a, button, input, img')) {
                            return;
                        }
                        e.stopPropagation();
                        setIsEditingLocalBody(true);
                    }}
                >
                    {isEditingLocalBody ? (
                        <textarea
                            autoFocus
                            value={localBody}
                            placeholder={lnT('messages:addYourNoteHere')}
                            onChange={(e) => setLocalBody(e.target.value)}
                            onBlur={() => {
                                setIsEditingLocalBody(false);
                                handleLocalBodySave(localBody);
                            }}
                            style={{
                                width: '100%',
                                border: 'none',
                                background: 'transparent',
                                outline: 'none',
                                resize: 'none',
                                fontFamily: 'inherit',
                                fontSize: 'inherit',
                                color: 'inherit',
                                minHeight: '60px'
                            }}
                        />
                    ) : (
                        <>
                            {localBody ? (
                                shouldRenderLocalBodyAsPlainText ? (
                                    <CompactPlainTextPreview>{normalizedLocalBody}</CompactPlainTextPreview>
                                ) : (
                                    <CompactMarkdownPreview>
                                        <Markdown options={compactNoteMarkdownOptions}>{normalizedLocalBody}</Markdown>
                                    </CompactMarkdownPreview>
                                )
                            ) : (
                                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{lnT('common:addNote')}</div>
                            )}
                        </>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: 4,
                            color: noteMetaColor
                        }}
                    >
                        <FaRegStickyNote size={12} style={{ opacity: 0.5 }} />
                        {localBody && !isEditingLocalBody && (
                           <span style={{ fontSize: '10px', opacity: 0.5 }}>{lnT('messages:localNote')}</span>
                        )}
                    </div>
                </div>
            );
        }

        if (message.body)
            return (
                <div
                    style={{
                        color: noteTextColor,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: noteSurfaceBackground,
                        border: `1px solid ${noteSurfaceBorder}`,
                        borderRadius: 8,
                        padding: 6,
                        minWidth: 100
                    }}
                >
                    {shouldRenderMessageBodyAsPlainText ? (
                        <CompactPlainTextPreview>
                            {renderHighlightedSearchText(normalizedMessageBody, searchHighlightTerm)}
                        </CompactPlainTextPreview>
                    ) : (
                        <CompactMarkdownPreview>
                            {searchHighlightTerm ? (
                                highlightReactChildren(
                                    compiler(normalizedMessageBody, compactNoteMarkdownOptions),
                                    searchHighlightTerm
                                )
                            ) : (
                                <Markdown options={compactNoteMarkdownOptions}>{normalizedMessageBody}</Markdown>
                            )}
                        </CompactMarkdownPreview>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 4
                            // marginTop: 6
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            <FaRegStickyNote
                                size={14}
                                style={{ marginLeft: 4, color: noteMetaColor }}
                            ></FaRegStickyNote>
                            {bodySearchMatchCount > 0 ? (
                                <SearchMatchCountBadge title={lnT('messages:match_76f5e0e2', { value1: String(bodySearchMatchCount), value2: String(bodySearchMatchCount === 1 ? '' : 'es') })}>
                                    {bodySearchMatchCount > 9 ? '9+' : bodySearchMatchCount}{lnT('messages:match_8ebb8cc7')}{bodySearchMatchCount === 1 ? '' : lnT('messages:es')}
                                </SearchMatchCountBadge>
                            ) : null}
                        </div>
                        {!isBoardLayout ? (
                            <OpenNoteButton
                                $isStarred={isStarredMessage}
                                data-message-interactive="true"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    customAnalytics.message_action({
                                        'action': "Open",
                                        "type": "Note"
                                    }, message);
                                    if (typeof onEditMessage === 'function') {
                                        onEditMessage({
                                            ...message,
                                            previewMode: 'NOTE',
                                            loadContext: false
                                        });
                                        return;
                                    }

                                    setSelectedMessage({
                                        ...message,
                                        loadContext: false,
                                        previewMode: 'NOTE'
                                    });
                                }}
                            >{lnT('common:open')}</OpenNoteButton>
                        ) : null}
                    </div>
                </div>
            );
    };

    const renderSocialMetrics = () => {
        if (!message.socialMetrics) return null;
        
        const { like_count, retweet_count, reply_count } = message.socialMetrics;
        if (like_count === undefined && retweet_count === undefined && reply_count === undefined) return null;

        const formatCount = (num) => {
            if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
            if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
            return num;
        };

        return (
            <div style={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center', 
                gap: 16, 
                marginTop: 8,
                padding: '4px 0',
                opacity: 0.7,
                fontSize: 12,
                color: PRIMARY_COLOR_6
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <IoMdHeart size={14} />
                    <span>{formatCount(like_count || 0)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <IoMdRepeat size={14} />
                    <span>{formatCount(retweet_count || 0)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <IoMdChatbubbles size={14} />
                    <span>{formatCount(reply_count || 0)}</span>
                </div>
            </div>
        );
    };

    const getShownFolderTitle = () => {
        if (message.folder && message.folder.title) {
            if (messageFeatures && messageFeatures.includes(SHOW_OPTIONS.SHOW_FOLDER)) {
                return message.parentMessageId
                    ? lnT('messages:inThread_a19e8eb5', { value1: String(message.folder.title) })
                    : message.folder.title;
            }
        }
        return '';
    };

    const embedSection = React.useMemo(() => {
        if (message?.urlMeta?.[0]?.embed) {
            let margin = MARGIN;
            return (
                <div style={{ marginBottom: margin }}>
                    <EmbedSection
                        key={message.id}
                        embed={message.urlMeta?.[0]?.embed}
                        onClick={() => {
                            customAnalytics.message_action({
                                'action': "Open",
                                "type": "Embed"
                            }, message);
                        }}
                    />
                </div>
            );
        }
        return null;
    }, [lnT, message.id, message?.urlMeta?.[0]?.embed, message]); // Add dependencies

    const renderKeywords = () => {
        if (!message.keywords || message.keywords.length === 0) return null;
        return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                <IoMdPricetags size={12} color={PRIMARY_COLOR_4} style={{ marginTop: '2px' }} />
                {message.keywords.map((keyword, i) => (
                    <span key={i} style={{ 
                        fontSize: '10px', 
                        backgroundColor: 'rgba(0,0,0,0.03)', 
                        padding: '2px 6px', 
                        borderRadius: '4px',
                        color: PRIMARY_COLOR_6,
                        border: '1px solid rgba(0,0,0,0.05)'
                    }}>
                        #{keyword}
                    </span>
                ))}
            </div>
        );
    };

    const renderFolderSuggestion = () => {
        // Only show if there's a suggestion and it's different from current folder
        if (!message.suggestedFolderTitle || message.folder?.title === message.suggestedFolderTitle) return null;
        
        return (
            <div
                data-message-interactive="true"
                style={{
                marginTop: '8px', 
                padding: '6px 10px', 
                backgroundColor: 'rgba(43, 136, 217, 0.08)', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '11px',
                color: '#2b88d9',
                cursor: 'pointer',
                border: '1px dashed rgba(43, 136, 217, 0.3)'
            }}
            onClick={(e) => {
                e.stopPropagation();
                if (pendingMessage) {
                    return;
                }
                if (showMoveMessageModal) {
                    showMoveMessageModal(message, message.suggestedFolderTitle);
                }
            }}
            >
                <MdFolderOpen size={14} />
                <span>{lnT('messages:aiSuggestionMoveTo')}<strong>{message.suggestedFolderTitle}</strong></span>
                <span style={{ 
                    marginLeft: 'auto', 
                    backgroundColor: '#2b88d9', 
                    color: 'white', 
                    padding: '2px 8px', 
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '600'
                }}>{lnT('messages:apply')}</span>
            </div>
        );
    };

    const renderEmbedContent = () => {
        return embedSection;
    };

    const isPendingMessage = () => {
        const hasResolvedServerId = Boolean(message?._resolvedServerId);
        const isOptimistic =
            !hasResolvedServerId &&
            typeof message?.id === 'string' &&
            message.id.startsWith('temp-');
        const hasUploadMarker = Boolean(message?._isUploading || message?._uploadProgress !== undefined);
        const uploadStatus = message?._uploadStatus;
        const isUploadPending = hasUploadMarker && uploadStatus !== 'done' && uploadStatus !== 'complete' && uploadStatus !== 'error';
        return isOptimistic || isUploadPending;
    };

    const pendingMessage = isPendingMessage();

    const renderConversationCardContent = () => {
        const openingQuestion = String(message?.title || message?.body || 'Conversation').trim() || lnT('messages:conversation');
        const turnCount = typeof message?.threadChatCount === 'number' ? message.threadChatCount : 0;
        const lastActivityAt = message?.activityAt || message?.threadActivityUpdatedAt || message?.updatedAt || message?.createdAt;
        let lastActivityLabel = null;
        if (lastActivityAt) {
            const activityDate = new Date(lastActivityAt);
            if (!Number.isNaN(activityDate.getTime())) {
                lastActivityLabel = lnT('messages:ago', { value1: String(formatDistanceToNowStrict(activityDate)) });
            }
        }
        const folderTitle = getShownFolderTitle();

        return (
            <ConversationCardContent data-testid="conversation-card">
                <ConversationCardIcon aria-hidden="true">
                    <IoMdChatbubbles size={18} />
                </ConversationCardIcon>
                <ConversationCardBody>
                    <ConversationCardTitle>{openingQuestion}</ConversationCardTitle>
                    <ConversationCardMeta>
                        <span>{turnCount} {turnCount === 1 ? lnT('messages:turn') : lnT('messages:turns')}</span>
                        {lastActivityLabel ? (
                            <>
                                <ConversationCardMetaDot>&middot;</ConversationCardMetaDot>
                                <span>{lastActivityLabel}</span>
                            </>
                        ) : null}
                        {folderTitle ? (
                            <>
                                <ConversationCardMetaDot>&middot;</ConversationCardMetaDot>
                                <span>{folderTitle}</span>
                            </>
                        ) : null}
                    </ConversationCardMeta>
                </ConversationCardBody>
            </ConversationCardContent>
        );
    };

    const renderDateSection = () => {
        const folderTitle = getShownFolderTitle();
        const canShowFolderAction = Boolean(folderTitle && message?.folder);
        const openMessageInFolder = event => {
            event.preventDefault();
            event.stopPropagation();

            if (typeof onShowInFolder === 'function') {
                onShowInFolder(message);
                return;
            }

            // Most cross-folder views only render DefaultMessage. Keep the jump
            // self-contained there while letting parent views handle any overlay
            // cleanup through onShowInFolder when they need to.
            if (typeof setSelectedFolder === 'function') {
                setSelectedFolder(message.folder);
            }
            setSelectedMessage({
                ...message,
                previewMode: 'MESSAGE',
                loadContext: true,
                navigateToFolder: true,
                openNoteOnLoadContext: false
            });
        };
        const reminderMeta = activeReminderLabel || reminderNeedsReview ? (
            <MessageMetaReminder
                $isFiredReminder={isFiredReminder}
                role="button"
                tabIndex={0}
                data-message-interactive="true"
                onClick={openReminderPicker}
                onKeyDown={handleReminderMetaKeyDown}
            >
                {isFiredReminder ? (
                    <MdNotificationsActive size={13} color={GREEN} />
                ) : (
                    <FiBell size={11} color={FILE_BLUE} />
                )}
                <span>{reminderNeedsReview ? lnT('messages:chooseReminderTime') : activeReminderLabel}</span>
            </MessageMetaReminder>
        ) : null;

        return (
            <MessageMetaRow
                $isSelectionMode={isSelectionMode}
                $showDateAtBottom={showDateAtBottom}
            >
                {(showDateAtBottom && folderTitle) ||
                (visibleMentionEntities.length > 0 && canShowEntityTagsInMeta) ||
                (attachedArtifactGroups.length > 0 && canShowEntityTagsInMeta) ? (
                    <MessageMetaLeftGroup>
                        {folderTitle ? (
                            canShowFolderAction ? (
                                <MessageMetaFolderTitleButton
                                    type="button"
                                    data-message-interactive="true"
                                    onClick={openMessageInFolder}
                                    title={lnT('messages:showIn', { value1: String(message.folder.title) })}
                                >
                                    {folderTitle}
                                </MessageMetaFolderTitleButton>
                            ) : (
                                <MessageMetaFolderTitle>{folderTitle}</MessageMetaFolderTitle>
                            )
                        ) : null}
                        {canShowEntityTagsInMeta ? (
                            <EntityBadges
                                entities={visibleMentionEntities}
                                compact
                                maxToShow={3}
                                onEntityClick={(entity, event, group) => {
                                    const entityType = String(entity?.type || '').trim();
                                    if (group?.count > 1) {
                                        setSelectedEntityMentionGroup(group.entities);
                                        setShowEntityMentionsModal(true);
                                        return;
                                    }
                                    const entityMatchesArtifact =
                                        artifactTypes.length === 0 || artifactTypes.includes(entityType);
                                    if (hasAttachedArtifactContent && entityMatchesArtifact) {
                                        openArtifactPreviewFromTag(event);
                                        return;
                                    }
                                    setSelectedEntityMentionGroup(null);
                                    setShowEntityMentionsModal(true);
                                }}
                            />
                        ) : null}
                        {canShowEntityTagsInMeta ? attachedArtifactGroups.map(({ type, count }) => {
                            const singularLabel = ENTITY_LABELS[type] || lnT('common:extraction');
                            const displayLabel = count === 1 ? singularLabel : (ENTITY_PLURAL_LABELS[type] || lnT('common:extractions'));
                            return (
                                <MessageMetaArtifactTag
                                    key={type}
                                    type="button"
                                    data-message-interactive="true"
                                    $colors={ENTITY_COLORS[type] || ENTITY_COLORS.other}
                                    onClick={openArtifactPreviewFromTag}
                                    title={lnT('messages:openExtracted', { value1: String(count), value2: String(displayLabel.toLowerCase()) })}
                                >
                                    <span>{count} {displayLabel}</span>
                                </MessageMetaArtifactTag>
                            );
                        }) : null}
                    </MessageMetaLeftGroup>
                ) : null}
                {multipleSelect ? (
                    <MessageMetaRightGroup>
                        <MessageSharingIndicator message={message} disabled={forceReadOnly || isSharedFolderView} onOpenChange={setSharingDialogOpen} />
                        {reminderMeta}
                        <MessageMetaActionButton
                            onClick={e => {
                                e.stopPropagation();
                                setHoveringMessage(false);
                                setHoveringSelectTarget(false);
                                if (getHoveredMessage() === hoverInstanceId) {
                                    setHoveredMessage(null);
                                }
                                multipleSelect(e);
                            }}
                            onMouseEnter={() => {
                                setHoveringSelectTarget(true);
                            }}
                            onMouseLeave={() => {
                                setHoveringSelectTarget(false);
                            }}
                        >
                            {hoveringSelectTarget ? (
                                <MessageMetaActionLabel>{lnT('messages:select')}</MessageMetaActionLabel>
                            ) : (
                                <MessageMetaTime>
                                    {pendingMessage && <IoMdTime size={11} style={{ opacity: 0.72 }} />}
                                    <span>{date}</span>
                                </MessageMetaTime>
                            )}
                        </MessageMetaActionButton>
                    </MessageMetaRightGroup>
                ) : (
                    <MessageMetaRightGroup>
                        <MessageSharingIndicator message={message} disabled={forceReadOnly || isSharedFolderView} onOpenChange={setSharingDialogOpen} />
                        {reminderMeta}
                        {pendingMessage && <IoMdTime size={11} style={{ opacity: 0.72 }} />}
                        <span>{date}</span>
                    </MessageMetaRightGroup>
                )}
            </MessageMetaRow>
        );
    };

    const renderCheckboxSection = () => {
        if (hideCheckboxSection) {
            return null;
        }

        return (
            <div style={{}}>
                <CheckboxSection
                    disabled={readOnly || Object.keys(effectiveSelectedMessagesMap).length > 0 || pendingMessage}
                    hide={isSelectionMode && !message?.isCheckbox}
                    customShowPlus={!isSelectionMode && hoveringContainer && !pendingMessage}
                    message={message}
                    height={messageRef}
                ></CheckboxSection>
            </div>
        );
    };

    const renderDropdown = () => {
        if (hideQuickActions) {
            return null;
        }

        return (
            <MessageDropdown
                index={index}
                screenName={screenName}
                hide={Object.keys(effectiveSelectedMessagesMap).length > 0 || pendingMessage}
                hovering={hoveringContainer}
                message={message}
                disabled={pendingMessage}
                showThreadAction={showThreadAction}
                showDeleteModal={val => {
                    if (val) showDeleteModal(message);
                }}
                showMoveMessageModal={val => {
                    if (val) showMoveMessageModal(message);
                }}
                containerRef={messageRef}
                openMenu={val => {
                    if (typeof openMenu === 'function') {
                        openMenu(val);
                    }
                    setHoveringMessage(false);
                }}
                onSelectMessage={
                    multipleSelect
                        ? () => {
                              multipleSelect({
                                  stopPropagation() {
                                      return undefined;
                                  },
                                  shiftKey: false
                              });
                          }
                        : undefined
                }
                onEditMessage={onEditMessage}
                onReplyMessage={onReplyMessage}
                onShowInFolder={onShowInFolder}
                isSharedFolderView={isSharedFolderView}
            ></MessageDropdown>
        );
    };

    const openCurrentMessagePreview = React.useCallback(() => {
        const previewMessage = { ...message, previewMode: 'MESSAGE', loadContext: false };
        if (typeof onEditMessage === 'function') {
            onEditMessage(previewMessage);
        } else {
            setSelectedMessage(previewMessage);
        }
    }, [message, onEditMessage, setSelectedMessage]);

    const openPreviewFromHoverAction = event => {
        event.preventDefault();
        event.stopPropagation();
        openCurrentMessagePreview();
    };

    const openArtifactPreviewFromTag = event => {
        event?.preventDefault?.();
        event?.stopPropagation?.();

        if (isSelectionMode && multipleSelect) {
            multipleSelect(event);
            return;
        }

        customAnalytics.message_action({
            action: 'Open',
            type: 'Artifact'
        }, message);

        setSelectedMessage({
            ...message,
            // Places now live in the regular message preview's Artifacts section.
            // ARTIFACT is the legacy structured-content tab, not the place view.
            previewMode: 'MESSAGE',
            loadContext: false
        });
    };

    const renderThreadHint = () => {
        if (!canShowThreadHint) {
            return null;
        }

        const fallbackActionLabel =
            threadHintActionLabel && threadHintActionLabel !== 'Open thread'
                ? threadHintActionLabel
                : computedThreadHintActionLabel;
        const actionLabel = hasBothThreadModes ? lnT('common:open') : fallbackActionLabel;
        const ThreadHintIcon = hasBothThreadModes
            ? IoMdMenu
            : preferredThreadOpenMode === 'THREAD'
              ? IoMdRepeat
              : IoMdChatbubbles;
        const openThreadTarget = mode => {
            const threadHintTargetMessage = {
                ...message,
                previewMode: mode
            };

            if (typeof onThreadHintClick === 'function') {
                onThreadHintClick(threadHintTargetMessage);
                return;
            }
            setSelectedMessage(threadHintTargetMessage);
        };

        return (
            <ThreadHintButton
                role="button"
                tabIndex={0}
                $visible={canShowThreadHint}
                $isStarred={message.isStarred}
                $active={isThreadHintPinnedOpen}
                $compactSpacing={compactThreadHintSpacing}
                onMouseLeave={() => setIsThreadHintPinnedOpen(false)}
                onBlur={() => setIsThreadHintPinnedOpen(false)}
                onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isSelectionMode && multipleSelect) {
                        multipleSelect(e);
                        return;
                    }
                    setIsThreadHintPinnedOpen(true);
                    openThreadTarget(preferredThreadOpenMode);
                }}
                onKeyDown={event => {
                    if (event.key !== 'Enter' && event.key !== ' ') {
                        return;
                    }
                    event.preventDefault();
                    event.stopPropagation();
                    setIsThreadHintPinnedOpen(true);
                    openThreadTarget(preferredThreadOpenMode);
                }}
            >
                <ThreadHintIcon size={14} />
                <ThreadHintLabelSwap>
                    <ThreadHintPrimaryLabel $active={isThreadHintPinnedOpen}>
                        {actionLabel}
                    </ThreadHintPrimaryLabel>
                    <ThreadHintHoverLabel $active={isThreadHintPinnedOpen}>
                        {hasBothThreadModes ? (
                            <ThreadHintChoiceRow>
                                <ThreadHintChoiceButton
                                    type="button"
                                    $isStarred={message.isStarred}
                                    onClick={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setIsThreadHintPinnedOpen(true);
                                        openThreadTarget('CHAT');
                                    }}
                                >{lnT('messages:chat')}</ThreadHintChoiceButton>
                                <ThreadHintChoiceButton
                                    type="button"
                                    $isStarred={message.isStarred}
                                    onClick={event => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        setIsThreadHintPinnedOpen(true);
                                        openThreadTarget('THREAD');
                                    }}
                                >{lnT('messages:thread')}</ThreadHintChoiceButton>
                            </ThreadHintChoiceRow>
                        ) : (
                            actionLabel
                        )}
                    </ThreadHintHoverLabel>
                </ThreadHintLabelSwap>
            </ThreadHintButton>
        );
    };
    return (
        <>
            <MessageContainer
                id={message.id}
                onClickCapture={event => {
                    if (event.currentTarget.contains(event.target) && !event.target?.closest?.('[data-message-select]') && !isSelectionMode && !readOnly && !isSharedFolderView && !message.deletedAt) {
                        recordMessageView(apolloClient, message.id);
                    }
                }}
                onAuxClickCapture={event => {
                    if (event.button === 1 && event.target?.closest?.('a') && !isSelectionMode && !readOnly && !isSharedFolderView && !message.deletedAt) {
                        recordMessageView(apolloClient, message.id);
                    }
                }}
                $isHovered={hoveringContainer}
                ref={node => {
                    messageRef.current = node;
                    if (disableThreadAttachTarget) {
                        return;
                    }
                    threadDrop(node);
                }}
                onMouseEnter={() => {
                    if (!isHoverSuppressed && Object.keys(effectiveSelectedMessagesMap).length === 0) {
                        setHoveringContainer(true);
                        setHoveredMessage(hoverInstanceId);
                    }
                }}
                onMouseLeave={() => {
                    if (Object.keys(effectiveSelectedMessagesMap).length === 0) {
                        clearHoverState();
                    }
                }}
                onClick={e => {
                    if (e.target?.tagName === 'A' || e.target?.tagName === 'BUTTON') {
                        e.stopPropagation();
                        return;
                    }

                    const interactiveTarget = e.target?.closest?.(
                        'a, button, input, textarea, select, [role="button"], [data-message-interactive="true"], [data-message-media-interactive="true"]'
                    );

                    if (interactiveTarget) {
                        return;
                    }
                    if ((message?.referencedMessageId || message?.parentMessageId) && !isSelectionMode) {
                        e.stopPropagation();
                        openCurrentMessagePreview();
                        return;
                    }

                    if (typeof onMessageClick === 'function') {
                        e.stopPropagation();
                        onMessageClick(e);
                    }
                }}
                onDoubleClick={e => {
                    if (typeof onReplyMessage !== 'function' || message?.deletedAt) {
                        return;
                    }

                    if (e.target?.tagName === 'A' || e.target?.tagName === 'BUTTON') {
                        e.stopPropagation();
                        return;
                    }

                    const interactiveTarget = e.target?.closest?.(
                        'a, button, input, textarea, select, [role="button"], [data-message-interactive="true"], [data-message-media-interactive="true"]'
                    );

                    if (interactiveTarget) {
                        return;
                    }

                    if (!e.target?.closest?.('[data-testid="message-wrapper"]')) {
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();
                    onReplyMessage(message);
                }}
            >
                {renderCheckboxSection()}
                <MessageWrapper
                    data-testid="message-wrapper"
                    $hasVideoAttachment={hasVideoAttachment}
                    $videoBubbleWidth={videoBubbleWidth}
                    $hasImageAttachment={hasImageAttachment}
                    $imageBubbleWidth={imageBubbleWidth}
                    $isStarred={message.isStarred}
                    $isSelectionMode={isSelectionMode}
                    $isHovered={hoveringContainer}
                    $hasExpandedTitleLink={hasExpandedTitleLink}
                    $showSelectOutline={hoveringSelectTarget && Boolean(multipleSelect)}
                    $showThreadDropOutline={canDropOnThreadTarget}
                    $isThreadDropActive={isOverThreadDropTarget}
                    $showDateAtBottom={showDateAtBottom}
                    $layoutMode={layoutMode}
                    $suppressSelectionHoverOutline={suppressSelectionHoverOutline}
                    $suppressBoardHoverHighlight={suppressBoardHoverHighlight}
                    $isConversationRoot={isConversationRoot}
                >
                    <MessageDiv
                        ref={drag}
                        holdingKeys={holdingKeys && keys.Meta}
                        hovering={hoveringContainer || hasExpandedTitleLink}
                        $disableTextSelection={isSelectionMode}
                        onClickCapture={handleSelectionModeInteractiveClickCapture}
                        onMouseEnter={() => {
                            if (!isHoverSuppressed && Object.keys(effectiveSelectedMessagesMap).length === 0) setHoveringMessage(true);
                        }}
                        onMouseLeave={() => {
                            if (Object.keys(effectiveSelectedMessagesMap).length === 0) setHoveringMessage(false);
                        }}
                        onMouseDownCapture={event => {
                            if (isSelectionMode && event.shiftKey) {
                                event.preventDefault();
                            }
                        }}
                        keys={keys}
                        style={{
                            opacity
                        }}
                        dir="auto"
                    >
                        <MessageContent>
                            {isConversationRoot ? (
                                renderConversationCardContent()
                            ) : (
                                <>
                                    {!hideMessageReference && (
                                        <MessageReferencePreview
                                            message={message}
                                            onActivate={event => {
                                                if (isSelectionMode && multipleSelect) {
                                                    multipleSelect(event);
                                                } else {
                                                    openCurrentMessagePreview();
                                                }
                                            }}
                                        />
                                    )}
                                    {(layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.image !== false) && renderImageContent()}
                                    {(layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.file !== false) && renderAWSContent()}
                                    {/* {renderEmbedContent()} */}
                                    {(layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.url !== false) && renderUrlMeta()}
                                    {!message.email && (layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.title !== false) && renderTitleContent()}
                                    <EmailPreviewPlugin message={message} isStarred={Boolean(message.isStarred)} onOpenVideoWorkspace={onOpenVideoWorkspace} />
                                    {(layoutMode !== 'masonry' || !masonryCardFields || masonryCardFields.body !== false) ? (
                                        <>
                                            {renderBodyContent()}
                                            {!isXBookmark && message.body ? (
                                                <SearchBodyMatchSnippet
                                                    excerpt={bodySearchMatchExcerpt}
                                                    searchTerm={searchHighlightTerm}
                                                />
                                            ) : null}
                                        </>
                                    ) : null}
                                    <SearchMatchSnippet
                                        message={message}
                                        searchTerm={searchHighlightTerm}
                                        hiddenFields={searchSnippetHiddenFields}
                                    />
                                    {!hideEventArtifacts && !message.event && <ExtractedArtifactsSection artifacts={(message.artifacts || []).filter(artifact => artifact.type === 'event')} />}
                                    <EventSection message={message} readOnly={readOnly} onEditReminders={() => setShowReminderPicker(true)} />
                                    {renderSocialMetrics()}
                                    {matchedEntityTypes && matchedEntityTypes.length > 0 ? (
                                        <EntityBadges
                                            entities={(message.entities || []).filter(e => matchedEntityTypes.includes(e?.type))}
                                            maxToShow={5}
                                            showStackIcon={showEntityStackIcon}
                                        />
                                    ) : null}
                                    {/* Intentionally hidden in message card for now; keep for future search/UX use */}
                                    {/* {renderKeywords()} */}
                                    {renderFolderSuggestion()}
                                    {renderThreadHint()}
                                    {(sharingDialogOpen || showDateAtBottom || visibleMentionEntities.length > 0 || reminderNeedsReview || message.sharingStatus?.shareCount > 0 || message.sharingStatus?.isPublished) && renderDateSection()}
                                </>
                            )}
                        </MessageContent>
                    </MessageDiv>
                </MessageWrapper>
                {showHoverPreviewAction && !pendingMessage ? (
                    <MessageHoverPreviewButton
                        type="button"
                        aria-label={lnT('messages:openPreview')}
                        title={lnT('messages:openPreview')}
                        data-message-interactive="true"
                        $visible={hoveringContainer}
                        onClick={openPreviewFromHoverAction}
                    >
                        <FiEdit2 size={13} />
                    </MessageHoverPreviewButton>
                ) : null}
                {!isUploading && renderDropdown()}
                {holdingKeys && keys.Meta ? (
                    <>
                        <a data-tip data-for="lockIcon">
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: 4,
                                    borderRadius: 8,
                                    backgroundColor: 'rgba(255,255,255,.4)',
                                    cursor: 'pointer',
                                    width: 24,
                                    height: 24
                                }}
                            >
                                <div>
                                    <MdLock size={16} color={PRIMARY_COLOR_8}></MdLock>
                                </div>
                            </div>
                        </a>
                        <ReactTooltip place="right" type="dark" effect="solid" id="lockIcon">
                            <span>{lnT('messages:messageLockedYouCanSelectAnyText')}</span>
                        </ReactTooltip>
                    </>
                ) : null}
                {/* <div style={{ flex: 1 }} /> */}
            </MessageContainer>
            <SocialNetworkModal
                isOpen={showSocialModal}
                onClose={() => setShowSocialModal(false)}
                username={selectedUsername}
                onSelectNetwork={handleSocialNetworkSelect}
            />
            <StockTickerModal
                isOpen={showStockTickerModal}
                onClose={() => setShowStockTickerModal(false)}
                symbol={selectedStockTicker}
                onSelectPlatform={(url) => {
                    setShowStockTickerModal(false);
                    customAnalytics.message_action({
                        'action': "Open",
                        "type": "StockTicker",
                        "url": url
                    }, message);
                    
                    // Use the same URL opening utility
                    openUrlInBrowser(url);
                }}
            />
            <Modal
                isOpen={showEntityMentionsModal}
                onRequestClose={() => {
                    setShowEntityMentionsModal(false);
                    setSelectedEntityMentionGroup(null);
                }}
                style={entityMentionsModalStyles}
                contentLabel={lnT('messages:messageMentions')}
            >
                <EntityMentionsModalShell>
                    <EntityMentionsModalClose
                        type="button"
                        onClick={() => {
                            setShowEntityMentionsModal(false);
                            setSelectedEntityMentionGroup(null);
                        }}
                        aria-label={lnT('messages:closeMentions')}
                    >
                        <FiX size={18} />
                    </EntityMentionsModalClose>
                    <EntityMentionsSection
                        entities={selectedEntityMentionGroup || message?.entities}
                        messageId={message?.id}
                        canRemove={!readOnly && !message?.deletedAt}
                        inModal
                        onSearch={entity => {
                            const searchTerm = String(entity?.name || '').trim();
                            if (!searchTerm) return;
                            const searchEngine = getStoredSearchEngine();
                            openUrlInBrowser(getSearchUrl(searchTerm, searchEngine));
                            customAnalytics.ui_action({
                                action: 'Entity Mention Search',
                                screen: 'DefaultMessage',
                                entity_type: entity?.type || 'other',
                                engine: searchEngine?.name || 'default'
                            });
                        }}
                    />
                </EntityMentionsModalShell>
            </Modal>
            <ConfirmModal
                isOpen={pendingThreadAttachCount > 0}
                title={pendingThreadAttachCount > 1 ? lnT('messages:moveMessagesToThread') : lnT('messages:moveMessageToThread')}
                description={
                    pendingThreadAttachCount > 1
                        ? lnT('messages:moveMessagesIntoTheyWillBeAdded', { value1: String(pendingThreadAttachCount), value2: String(threadAttachTargetLabel) })
                        : lnT('messages:moveThisMessageIntoItWillBe', { value1: String(threadAttachTargetLabel) })
                }
                closeButtonText={lnT('common:cancel')}
                actionButtonText={pendingThreadAttachCount > 1 ? lnT('messages:moveToThread') : lnT('messages:moveToThread')}
                closeModal={handleThreadAttachConfirmClose}
            />
            <ReminderPickerModal
                isOpen={showReminderPicker}
                initialReminderAt={effectiveReminderTimestamp}
                initialReminderFiredAt={message?.reminderFiredAt}
                initialDeliveryType={message?.reminderDeliveryType}
                initialReminderMessage={message?.reminderMessage}
                initialAlerts={message?.alerts}
                loading={setMessageReminderLoading}
                onClose={() => setShowReminderPicker(false)}
                onSubmit={submitReminder}
            />
        </>
    );
};

export const MARGIN = 8;

const EntityMentionsModalShell = styled.div`
    position: relative;
    padding: 20px;
    color: rgba(255, 255, 255, 0.9);
`;

const EntityMentionsModalClose = styled.button`
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 1;
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.66);
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.11);
        color: ${WHITE};
    }

    &:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.72);
        outline-offset: 2px;
    }
`;

const MessageContainer = styled.div`
    display: flex;
    flex-direction: row;
    // align-items: center;
    // cursor: pointer;
    flex: 1;
    // width: 100%;
    position: relative;
    overflow: visible;
    z-index: ${props => (props.$isHovered ? 1000 : 'auto')};
`;

const MessageWrapper = styled.div`
    position: relative;
    overflow: ${props => (isGridCardLayout(props.$layoutMode) || props.$isHovered || props.$hasExpandedTitleLink ? 'visible' : 'hidden')};
    isolation: isolate;
    contain: ${props => (isGridCardLayout(props.$layoutMode) || props.$isHovered || props.$hasExpandedTitleLink ? 'none' : 'paint')};
    display: flex;
    flex-direction: row;
    min-width: ${props => {
        if (isGridCardLayout(props.$layoutMode)) return '0';
        if (props.$hasImageAttachment && props.$imageBubbleWidth) {
            return '0';
        }
        if (props.$hasVideoAttachment && props.$videoBubbleWidth) {
            return '0';
        }
        return '190px';
    }};
    width: ${props => {
        if (isGridCardLayout(props.$layoutMode)) return '100%';
        if (props.$hasVideoAttachment) {
            return `min(100%, ${props.$videoBubbleWidth || 600}px)`;
        }
        if (props.$hasImageAttachment && props.$imageBubbleWidth) {
            return `min(100%, ${props.$imageBubbleWidth}px)`;
        }
        return 'auto';
    }};
    max-width: ${props => (isGridCardLayout(props.$layoutMode) ? '100%' : props.$hasVideoAttachment ? '600px' : '500px')};
    margin-right: ${props => (isGridCardLayout(props.$layoutMode) || props.$showDateAtBottom ? '0' : '40px')};
    z-index: ${props =>
        (props.$isHovered || props.$isThreadDropActive || props.$showThreadDropOutline || props.$showSelectOutline)
            ? 1
            : 0};
    border: 1px solid
        ${props => {
            if (props.$isThreadDropActive) {
                return GREEN;
            }
            if (props.$showThreadDropOutline) {
                return GREEN_OP;
            }
            if (props.$showSelectOutline) {
                return GREEN_OP;
            }
            if (props.$isSelectionMode && props.$isHovered && !props.$suppressSelectionHoverOutline) {
                return GREEN_OP;
            }
            if (props.$layoutMode === 'board' && props.$isHovered && !props.$suppressBoardHoverHighlight) {
                return 'rgba(255, 255, 255, 0.18)';
            }
            return props.$isStarred ? 'rgba(35, 39, 42, 0.08)' : PALE_WHITE_1;
        }};
    border-radius: 8px;
    color: ${props => (props.$isStarred ? PRIMARY_COLOR_8 : 'rgba(255, 255, 255, 0.9)')};
    box-shadow: ${props =>
        props.$isThreadDropActive
            ? `0 0 0 2px ${GREEN_OP_2}`
            : (props.$showThreadDropOutline ||
                props.$showSelectOutline ||
                (props.$isSelectionMode && props.$isHovered && !props.$suppressSelectionHoverOutline))
              ? `0 0 0 1px ${GREEN_OP_2}`
              : 'none'};
    transform: ${props =>
        props.$layoutMode === 'board' && props.$isHovered && !props.$suppressBoardHoverHighlight
            ? 'translateY(-1px)'
            : 'none'};
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;

    &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        clip-path: inset(0 round 8px);
        background-color: ${props => {
            if (props.$isConversationRoot) {
                return 'rgba(99, 102, 241, 0.14)';
            }
            if (props.$isStarred) {
                return STARRED;
            }
            if (props.$layoutMode === 'board' && props.$isHovered && !props.$suppressBoardHoverHighlight) {
                return 'rgba(35, 39, 42, 0.84)';
            }
            return 'rgba(35, 39, 42, 0.72)';
        }};
        backdrop-filter: saturate(160%) blur(8px);
        -webkit-backdrop-filter: saturate(160%) blur(8px);
        pointer-events: none;
        z-index: 0;
    }
`;

const MessageHoverPreviewButton = styled.button`
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 4;
    width: 26px;
    height: 26px;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 7px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.82);
    background: rgba(23, 23, 23, 0.78);
    backdrop-filter: saturate(160%) blur(10px);
    -webkit-backdrop-filter: saturate(160%) blur(10px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.24);
    cursor: pointer;
    opacity: ${props => (props.$visible ? 1 : 0)};
    pointer-events: ${props => (props.$visible ? 'auto' : 'none')};
    transform: translateY(${props => (props.$visible ? '0' : '-2px')});
    transition: opacity 0.12s ease, transform 0.12s ease, background 0.12s ease, border-color 0.12s ease;

    &:hover {
        color: ${WHITE};
        border-color: rgba(255, 255, 255, 0.28);
        background: rgba(35, 39, 42, 0.92);
    }

    &:focus-visible {
        opacity: 1;
        pointer-events: auto;
        outline: 2px solid rgba(255, 255, 255, 0.72);
        outline-offset: 2px;
    }
`;

const MessageDiv = styled.div`
    display: flex;
    align-items: flex-start;
    flex-direction: row;
    padding: 8px;
    cursor: pointer;
    width: 100%;
    word-break: break-word;
    user-select: ${props => (props.$disableTextSelection ? 'none' : 'text')};
    -webkit-user-select: ${props => (props.$disableTextSelection ? 'none' : 'text')};
    -moz-user-select: ${props => (props.$disableTextSelection ? 'none' : 'text')};
    -ms-user-select: ${props => (props.$disableTextSelection ? 'none' : 'text')};
    position: relative;
    z-index: 1;
    overflow-x: ${props => props.hovering ? 'visible' : 'hidden'};
    overflow-y: ${props => props.hovering ? 'visible' : 'visible'};
    // padding-top: 12px;
    // padding-bottom: 12px;
    // padding-right: 12px;
    // padding-left: 12px;
    // white-space: pre-wrap;
    // line-height: 150%;
`;

// ${props => {
//     if (props.holdingKeys && props.keys.Meta) {
//         return `text`;
//     }
// }};

const MessageContent = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
`;

const ConversationCardContent = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
    max-width: 100%;
`;

const ConversationCardIcon = styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(99, 102, 241, 0.22);
    color: rgba(199, 202, 255, 0.95);
`;

const ConversationCardBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const ConversationCardTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    line-height: 140%;
    color: rgba(255, 255, 255, 0.92);
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
`;

const ConversationCardMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: ${SECONDARY_TEXT};
`;

const ConversationCardMetaDot = styled.span`
    color: ${SECONDARY_TEXT};
`;

const MessageMetaRow = styled.div`
    color: inherit;
    opacity: 0.72;
    padding-top: 8px;
    padding-bottom: 2px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    min-width: 0;
    width: 100%;
    flex-wrap: nowrap;
    border-radius: 6px;
    transition: opacity 0.15s ease;
`;

const MessageMetaFolderTitle = styled.div`
    min-width: 0;
    flex: 0 1 auto;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MessageMetaFolderTitleButton = styled.button`
    min-width: 0;
    flex: 0 1 auto;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border: 0;
    padding: 0;
    color: inherit;
    font: inherit;
    line-height: inherit;
    text-align: left;
    background: transparent;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        text-decoration: underline;
    }
`;

const MessageMetaLeftGroup = styled.div`
    min-width: 0;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const MessageMetaArtifactTag = styled.button`
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    appearance: none;
    padding: 1px 5px;
    border: 1px solid ${({ $colors }) => $colors.border};
    border-radius: 4px;
    background: ${({ $colors }) => $colors.bg};
    color: ${({ $colors }) => $colors.text};
    font: inherit;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
        filter: brightness(1.14);
    }

    &:focus-visible {
        outline: 2px solid currentColor;
        outline-offset: 2px;
    }
`;

const MessageMetaTime = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
`;

const MessageMetaRightGroup = styled.div`
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
`;

const MessageMetaReminder = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 3px;
    flex-shrink: 0;
    margin-right: 1px;
    color: ${props => (props.$isFiredReminder ? GREEN : FILE_BLUE)};
    cursor: pointer;
    padding: 2px 5px;
    border-radius: 6px;
    transition: background 0.15s ease, opacity 0.15s ease;

    &:hover {
        background: ${props => (props.$isFiredReminder ? GREEN_OP_2 : 'rgba(100, 149, 237, 0.12)')};
    }

    &:focus-visible {
        outline: 2px solid ${props => (props.$isFiredReminder ? GREEN_OP_2 : 'rgba(100, 149, 237, 0.32)')};
        outline-offset: 2px;
        border-radius: 4px;
    }
`;

const MessageMetaActionButton = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
    padding: 2px 6px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: inherit;
    cursor: pointer;
    transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;
    outline: none;

    &:hover {
        opacity: 1;
        color: ${GREEN};
        background: rgba(255, 255, 255, 0.08);
    }
`;

const MessageMetaActionLabel = styled.div`
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    white-space: nowrap;
`;

export const PreMessage = styled.div`
    word-break: break-word;
    white-space: pre-wrap;
    line-height: 150%;
    overflow-wrap: break-word;
    max-width: 100%;
    a {
        font-weight: bold;
        color: #2b88d9;
        text-decoration: none;
        word-break: break-all;
        overflow-wrap: break-word;
        max-width: 100%;
        display: inline-block;
    }
`;

const SearchSnippetRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
    margin-top: 6px;
    padding: 5px 7px;
    border-radius: 7px;
    background: rgba(255, 214, 102, 0.08);
    border: 1px solid rgba(255, 214, 102, 0.2);
    color: rgba(255, 255, 255, 0.88);
    font-size: 12px;
    line-height: 16px;
`;

const SearchSnippetLabel = styled.span`
    flex: 0 0 auto;
    color: rgba(255, 255, 255, 0.58);
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
`;

const SearchSnippetText = styled.span`
    min-width: 0;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
`;

const SearchMatchCountBadge = styled.span`
    flex: 0 0 auto;
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(255, 214, 102, 0.14);
    border: 1px solid rgba(255, 214, 102, 0.28);
    color: rgba(255, 255, 255, 0.62);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
`;

const SearchMatchExcerptPrefix = styled.span`
    opacity: 0.5;
`;

const compactMarkdownSurfaceCss = css`
    word-break: break-word;
    overflow-wrap: anywhere;

    > *:first-child {
        margin-top: 0 !important;
    }

    > *:last-child {
        margin-bottom: 0 !important;
    }

    p,
    li {
        white-space: pre-wrap;
    }

    ul,
    ol {
        margin-left: 0;
    }

    img,
    table,
    pre {
        max-width: 100%;
    }
`;

const TitleMarkdownPreview = styled.div`
    ${compactMarkdownSurfaceCss}
`;

const CompactMarkdownPreview = styled.div`
    max-height: 14.5em;
    overflow: hidden;
    padding: 1px;
    ${compactMarkdownSurfaceCss}
`;

const CompactPlainTextPreview = styled.div`
    max-height: 14.5em;
    overflow: hidden;
    padding: 1px;
    white-space: pre-wrap;
    word-break: break-word;
    color: inherit;
`;

const LinkButton = styled.a`
    cursor: pointer;
    color: #2b88d9;
    font-weight: bold;
    text-decoration: none;
    word-break: break-all;
    overflow-wrap: break-word;
    width: 100%;
    max-width: 100%;
    display: inline-block;
`;

const ExpandableLinkContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    max-width: 100%;
`;

const ClampedLinkText = styled.span`
    display: ${props => (props.$isExpanded ? 'block' : '-webkit-box')};
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${props => (props.$isExpanded ? 'initial' : props.$maxLines)};
    overflow: hidden;
    white-space: ${props => (props.$isExpanded ? 'pre-wrap' : 'normal')};
    word-break: break-all;
    overflow-wrap: break-word;
    max-width: 100%;
`;

const LinkExpandToggle = styled.button`
    border: 0;
    background: transparent;
    padding: 4px 0 0;
    width: 100%;
    text-align: left;
    color: #a7adb5;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        color: #c1c6cd;
        text-decoration: underline;
    }
`;

const OpenNoteButton = styled.button`
    background: none;
    border: none;
    color: ${props => (props.$isStarred ? PRIMARY_COLOR_4 : '#2b88d9')};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
    text-decoration: none;
    
    &:hover {
        text-decoration: underline;
    }
`;

const ThreadHintLabelSwap = styled.span`
    display: grid;
    align-items: center;
    justify-items: start;
    text-align: left;
    flex: 1;
    min-width: 0;
`;

const ThreadHintPrimaryLabel = styled.span`
    grid-area: 1 / 1;
    opacity: ${props => (props.$active ? 0 : 1)};
    transition: opacity 0.14s ease;
    white-space: nowrap;
`;

const ThreadHintHoverLabel = styled.span`
    grid-area: 1 / 1;
    opacity: ${props => (props.$active ? 1 : 0)};
    transition: opacity 0.14s ease;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const ThreadHintChoiceRow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const ThreadHintChoiceButton = styled.button`
    border: 1px solid
        ${props => (props.$isStarred ? 'rgba(0, 0, 0, 0.16)' : 'rgba(255, 255, 255, 0.2)')};
    background: ${props => (props.$isStarred ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.12)')};
    color: inherit;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    padding: 5px 8px;
    min-width: 56px;
    min-height: 24px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: ${props => (props.$isStarred ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.2)')};
    }
`;

const ThreadHintButton = styled.div`
    width: 100%;
    border: 1px solid transparent;
    background: ${props =>
        props.$active
            ? props.$isStarred
                ? 'rgba(0, 0, 0, 0.08)'
                : 'rgba(255, 255, 255, 0.11)'
            : props.$isStarred
              ? 'rgba(0, 0, 0, 0.04)'
              : 'rgba(255, 255, 255, 0.06)'};
    color: ${props => (props.$isStarred ? 'rgba(35, 39, 42, 0.72)' : 'rgba(255, 255, 255, 0.78)')};
    font-size: 14px;
    line-height: 1.35;
    border-radius: 10px;
    margin-top: ${props => (props.$visible ? (props.$compactSpacing ? '0' : '10px') : '0')};
    padding: ${props => (props.$visible ? '8px 10px' : '0 10px')};
    max-height: ${props => (props.$visible ? '40px' : '0')};
    opacity: ${props => (props.$visible ? 1 : 0)};
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: opacity 0.16s ease, max-height 0.16s ease, margin-top 0.16s ease, padding 0.16s ease,
        border-color 0.16s ease, background 0.16s ease;

    &:hover {
        background: ${props => (props.$isStarred ? 'rgba(0, 0, 0, 0.07)' : 'rgba(255, 255, 255, 0.1)')};
    }

    &:hover ${ThreadHintPrimaryLabel},
    &:focus-visible ${ThreadHintPrimaryLabel} {
        opacity: 0;
    }

    &:hover ${ThreadHintHoverLabel},
    &:focus-visible ${ThreadHintHoverLabel} {
        opacity: 1;
    }
`;

const EntityEnrichmentCard = styled.div`
    display: flex;
    flex-direction: ${({ $isGrid }) => $isGrid ? 'column' : 'row'};
    align-items: ${({ $isGrid }) => $isGrid ? 'stretch' : 'flex-start'};
    gap: ${({ $isGrid }) => $isGrid ? '0' : '12px'};
    padding: ${({ $isGrid }) => $isGrid ? '0' : '10px'};
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(255, 255, 255, 0.04);
    overflow: hidden;
    min-width: 0;
`;

const EntityEnrichmentPoster = styled.img`
    display: block;
    object-fit: cover;
    background: rgba(255, 255, 255, 0.06);
    cursor: pointer;
    transition: opacity 0.15s ease;

    ${({ $isGrid, $aspectRatio }) => $isGrid ? `
        width: 100%;
        aspect-ratio: ${$aspectRatio ? `1 / ${$aspectRatio}` : '2 / 3'};
        border-radius: 0;
        flex: none;
    ` : `
        flex: 0 0 auto;
        width: 100px;
        height: ${$aspectRatio ? `${Math.round(100 * $aspectRatio)}px` : '148px'};
        border-radius: 6px;
    `}

    &:hover {
        opacity: 0.85;
    }
`;

const EntityEnrichmentBody = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: ${({ $isGrid }) => $isGrid ? '10px' : '0'};
`;

const EntityEnrichmentTitle = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.92);
    line-height: 1.35;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EntityEnrichmentDesc = styled.span`
    font-size: 11px;
    color: rgba(255, 255, 255, 0.55);
    line-height: 1.5;
    display: -webkit-box;
    -webkit-line-clamp: ${({ $isGrid }) => $isGrid ? '2' : '3'};
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const EntityEnrichmentSource = styled.span`
    font-size: 10px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.35);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: auto;
`;

const EntityEnrichmentLink = styled.a`
    font-size: 10px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-top: auto;
    text-decoration: none;

    &:hover {
        color: rgba(255, 255, 255, 0.75);
        text-decoration: underline;
    }
`;

// Loading is a separate branch so placeholder renders never run message queries,
// drag/drop setup or effects, and switching loading does not change hook order.
export default function DefaultMessage({ loading = false, ...props }) {
    return loading ? <DefaultMessageSkeleton {...props} /> : <DefaultMessageContent {...props} />;
}

function DefaultMessageSkeleton({ layoutMode, showDateAtBottom, index = 0, loadingLabel }) {
    const { t } = useTranslation();
    return <SkeletonContainer role="status" aria-label={loadingLabel || t('folders:loadingMessages')}>
        <SkeletonWrapper $layoutMode={layoutMode} $showDateAtBottom={showDateAtBottom} $index={index}>
            <MessageDiv as="div" style={{ cursor: 'default' }} aria-hidden="true">
                <MessageContent>
                    <SkeletonLine $width="88%" />
                    <SkeletonLine $width={index % 2 ? '58%' : '76%'} />
                    {index % 2 === 0 ? <SkeletonLine $width="46%" /> : null}
                    {showDateAtBottom ? <SkeletonMeta><SkeletonLine $width="52px" /></SkeletonMeta> : null}
                </MessageContent>
            </MessageDiv>
        </SkeletonWrapper>
    </SkeletonContainer>;
}

const SkeletonContainer = styled(MessageContainer)`
    pointer-events: none;
    overflow-anchor: none;
    margin-bottom: 8px;
    animation: default-message-loading 1.4s ease-in-out infinite;
    @keyframes default-message-loading { 0%, 100% { opacity: .5; } 50% { opacity: 1; } }
    @media (prefers-reduced-motion: reduce) { animation: none; }
`;
const SkeletonWrapper = styled(MessageWrapper)`
    width: ${({ $layoutMode, $index }) => isGridCardLayout($layoutMode) ? '100%' : $index % 2 ? 'min(100%, 320px)' : 'min(100%, 440px)'};
    min-width: 0;
`;
const SkeletonLine = styled.div`
    height: 11px;
    width: ${({ $width }) => $width};
    max-width: 100%;
    border-radius: 4px;
    background: rgba(255,255,255,.12);
    & + & { margin-top: 9px; }
`;
const SkeletonMeta = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
    opacity: .5;
    > div { height: 8px; }
`;
